// Typed imports from node_modules
import _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { Component, Fragment } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  DropdownButton,
  Dropdown,
  FormCheck,
  Modal,
  OverlayTrigger,
  Popover,
  Tabs,
  Tab,
} from 'react-bootstrap';
import classNames from 'classnames';
import { ClipLoader } from 'react-spinners';
import * as Sentry from '@sentry/browser';

const Devtools = process.env.NODE_ENV !== 'production' ? require('mobx-react-devtools').default : Fragment;

import config from '../../config';

// PACCAR/Bond Loyalty Integration
import Loyalty from '../loyalty';
import CustomerSelect from '../loyalty/CustomerSelect';
import OfferPartList from '../loyalty/OfferPartList';
import { ILoyaltyOffer } from '../loyalty/typings/LoyaltyOffer';

// Local TS/TSX (Typed) Imports
import BackorderModal from './components/modals/BackorderModal';
import BackorderStatus from './components/modals/BackorderStatusModal';
import CODCrossReferenceSelectionModal from './components/modals/CODCrossReferenceSelectionModal';
import ConfirmationModal from './components/modals/ConfirmationModal';
import ConfirmationShipToSelectionModal from './components/modals/ConfirmationShipToSelectionModal';
import CreditHoldPostOpModal from './components/modals/CreditHoldPostOpModal';
import CreditInvoiceModal from './components/modals/CreditInvoiceModal';
import CustomerHistoryModal from './components/modals/CustomerHistoryModal';
import CustomerLookup, { ICustomerLookupRecord } from './components/lookups/CustomerLookup';
import CustomerVehiclesModal from './components/modals/CustomerVehiclesModal';
import EmailOptionsModal from './components/modals/EmailOptionsModal';
import FileNotFound from '../FileNotFound';
import FleetcrossModal from './components/modals/FleetcrossModal';
import FleetBillingPostOpModal from './components/modals/FleetBillingPostOpModal';
import InvoiceBuilder from './components/invoicebuilder/InvoiceBuilder';
import Label from './components/Label';
import LineItemEntry from './components/invoicebuilder/LineItemEntry';
import LostSaleReasonCodeSelectionModal from './components/modals/LostSaleReasonCodeSelectionModal';
import Model, { IShipToAddress, SavedListType } from './model/ShopKeeper.model';
import modal from './typings/Modal';
import OperationSelectionModal from './components/modals/OperationSelectorModal';
import PrinterSelectionModal from './components/modals/PrinterSelectionModal';
import PrinterSelectionBOModal from './components/modals/PrinterSelectionBOModal';
import PPCAuthenticator from './components/PPCAuthenticator';
import ProcessLoyaltyRedemptionsModal from './components/modals/ProcessLoyaltyRedemptionsModal';
import ScreenPrintModal from './components/modals/ScreenPrintModal';
import ShipToOverrideModal from './components/modals/ShipToOverrideModal';
import SubletUnpricedPOModal from './components/modals/SubletUnpricedPOModal';
import VariancePanelModal from './components/modals/VariancePanelModal';

// Interfaces
import { ICustomerParams } from './typings/CustomerParameters';

// Local JS (Untyped) Imports
import toastr from '../toastr';
import LoadingPage from '../LoadingPage';
import ReduxStore from '../../store';

// Styles
import 'react-contexify/dist/ReactContexify.min.css';
import '../../styles/shopkeeper.scss';

/* // Final betaList pre-GA for PRO1
const betaList = [
  999,   // local dev
  '999', // devqm
  '119',
  '131',
  '133',
  '151',
  '165',
  '188',
  '198',
  '199',
  '259',
  '260',
  '262',
  '263',
  '264',
  '266',
  '267',
  '268',
  '269',
  '270',
  '300',
  '321',
  '323',
  '324',
  '330',
];
*/

// Global sk object (developer tool acesss to sk.model, for example)
declare let sk: any;

enum Mode {
  BackOrder,
  InvoiceBuilder,
  InvoiceHistory,
}

interface IProps {
  confirmationNum?: string;
  seedNum?: string;
}

const getContainer = () => {
  return document.getElementById('shopkeeper');
};

@observer
class ShopKeeper extends Component<IProps, {}> {
  @observable activeMode: Mode = Mode.InvoiceBuilder;
  @observable devtools: boolean = false;
  @observable keyboardInterfaceActive: boolean = false;

  @observable showBackorderModal: boolean = false;
  @observable showCODCrossReferenceSelectionModal: boolean = false;
  @observable showConfirmationStatusModal: boolean = false;
  @observable showCustomerHistoryModal: boolean = false;
  @observable showCustomerVehiclesModal: boolean = false;
  @observable showDeleteBillOfMaterialsModal: boolean = false;
  @observable showEmailOptionsModal: boolean = false;
  @observable showFleetcrossModal: boolean = false;
  @observable showLoadSavedListModal: boolean = false;
  @observable showLostSaleReasonCodeSelectionModal: boolean = false;
  @observable showSaveBillOfMaterialsModal: boolean = false;
  @observable showOperationSelectionModal: boolean = false;
  @observable showPendingConfirmationsModal: boolean = false;
  @observable showSubletUnpricedPOModal: boolean = false;
  @observable showVariancePanel: boolean = false;

  appState: any; // ReduxStore

  // Used to preview features (by page) to a beta group
  betaLock: boolean = false;
  betaPage: string;

  backorderType: 'NOW' | 'DAILY';
  fleetcrossAutoInquire: boolean = false;
  fleetcrossSeed: string;
  initialUIRender: boolean = true;
  keyboardInterfaceTimerHandle = -1;
  lastCODCrossReferences: ICustomerLookupRecord[];
  model: Model;
  savedListIDSeed: string;
  savedListType: SavedListType;

  // UI References
  controlNumInputRef: HTMLInputElement;
  creditCardSaleInputRef: HTMLInputElement;
  customerLookupRef: CustomerLookup;
  deleteBOMListIDInputRef: HTMLInputElement;
  expandAllAlternativesInputRef: HTMLInputElement;
  freightInputRef: HTMLInputElement;
  fuelChargeInputRef: HTMLInputElement;
  glNumInputRef: HTMLInputElement;
  hideAdditionalDetailInputRef: HTMLInputElement;
  lineItemEntryRef: LineItemEntry;
  manualCreditAuthPpcInput: HTMLInputElement;
  manualCreditAuthPasswordInput: HTMLInputElement;
  poInputRef: HTMLInputElement;
  ppcAuthenticatorRef: PPCAuthenticator;
  receivedByInputRef: HTMLInputElement;
  roNumberInputRef: HTMLInputElement;
  saveBOMListIDInputRef: HTMLInputElement;
  saveBOMListTitleInputRef: HTMLInputElement;
  savedListIDInputRef: HTMLInputElement;
  transferConditionNumInputRef: HTMLInputElement;
  transferRoNumInputRef: HTMLInputElement;

  constructor(props: IProps) {
    super(props);
    const appState = (ReduxStore as any).getState();

    /* Example beta lock
    const clientNum = _.get(appState, 'app.config.clientNum', '');
    const pageName = _.get(appState, 'page.pageName', '');
    const userName = _.get(appState, 'app.user.username', '');

    if (
      ['pro1', 'PRO1', 'smm3', 'SMM3', 'sre2', 'SRE2'].includes(pageName) &&
      !(userName === 'dsisupport' || _.includes(betaList, clientNum))
    ) {
      console.error('Client not on PRO1/SMM3 beta list.');
      this.betaLock = true;
      this.betaPage = pageName;
      return;
    }
    */

    (window as any).sk = this;
    this.appState = appState;

    // TODO: make the model accessible via context rather than prop drilling
    const { confirmationNum, seedNum } = props;
    this.model = new Model(this, appState, { confirmationNum, seedNum });
  }

  // Register CTRL+ALT legacy keyboard input hook here
  componentDidMount() {
    if (!this.betaLock) {
      document.addEventListener('keydown', this.keyboardInterface);
    }
  }

  componentDidUpdate() {
    // This focuses to PPC on the first render post sysparams load indicator.
    if (this.model.initialized && this.initialUIRender) {
      this.ppcAuthenticatorRef.focus();
      this.initialUIRender = false;
    }
  }

  // Unregister CTRL+ALT legacy keyboard input hook here
  // Turn off any existing background PACCAR offer checks (inert on non-loyalty customers)
  componentWillUnmount() {
    if (!this.betaLock) {
      document.removeEventListener('keydown', this.keyboardInterface);
      this.model.clearLoyalty();
    }
  }

  displayLoyaltyCustomer = () => {
    let loyaltyDetails = '';
    const loyaltyParams = this.model.loyaltyCustomerDetails;

    const fullName = `${loyaltyParams.firstName} ${loyaltyParams.lastName}`.trim();
    const cityStateZip = `${loyaltyParams.city} ${loyaltyParams.state} ${loyaltyParams.zipCode}`.trim();

    loyaltyDetails += fullName ? `${fullName}<br />` : '';
    loyaltyDetails += loyaltyParams.address1 ? `${loyaltyParams.address1}<br />` : '';
    loyaltyDetails += loyaltyParams.address2 ? `${loyaltyParams.address2}<br />` : '';
    loyaltyDetails += cityStateZip ? `${cityStateZip}<br />` : '';
    loyaltyDetails += loyaltyParams.phoneNumber ? `${loyaltyParams.phoneNumber}<br />` : '';
    loyaltyDetails += loyaltyParams.emailAddress ? `${loyaltyParams.emailAddress}<br />` : '';

    return loyaltyDetails;
  };

  // This originally came from D3/QM as value marks for line endings.
  // Then it was switched to newlines.  Now it's seemingly back to value marks.
  // We'll move forward supporting both.  SK API splits on new lines still.
  displayMemo = () => {
    let res = '';
    this.model.customerParams.memoOrderScreen.forEach(line => {
      res += `${line.replace(/ý/g, '</br>')}</br>`;
    });
    if (this.model.customerParams.fleetBilled) {
      res += '**FLEET BILLED**';
    }
    return res;
  };

  displayShipTo = () => {
    let shipToDetails = '';
    const customerParams = this.model.customerParams;

    if (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode)) {
      shipToDetails += `<div class="sk-roCustomerDetailsHeader">#:${this.model.activeCustomerID}${
        this.model.po !== '' ? ` / PO: ${this.model.po}` : ''
      }</div>`;
    }

    shipToDetails += !!customerParams.name1.trim() ? `${customerParams.name1}<br />` : '';
    shipToDetails += !!customerParams.name2.trim() ? `${customerParams.name2}<br />` : '';
    shipToDetails += !!customerParams.name3.trim() ? `${customerParams.name3}<br />` : '';
    shipToDetails += !!customerParams.address.trim() ? `${customerParams.address}<br />` : '';
    shipToDetails += `${customerParams.city} ${customerParams.state} ${customerParams.zipcode}`;
    shipToDetails += !!customerParams.phone.trim() ? `<br />${customerParams.phone}` : '';
    shipToDetails += !!customerParams.email.trim() ? `<br />${customerParams.email}` : '';

    // 2 spaces only
    if (shipToDetails === '  ') {
      shipToDetails = 'Not Specified';
    }

    return shipToDetails;
  };

  displayShipToOverride = () => {
    let shipToDetails = '';

    // Custom ship to address variance provided?
    if (this.model.shipToAddress) {
      const x = this.model.shipToAddress;
      shipToDetails += !!x.name ? `${x.name}<br />` : '';
      shipToDetails += !!x.address ? `${x.address}<br />` : '';
      shipToDetails += `${x.city} ${x.state} ${x.zipCode.split('.')[0]}`;
      shipToDetails += !!x.phone ? `<br />${x.phone}` : '';

      // Otherwise, we'll derive display info from a full ICustomerParams object associated with shipToAccount.
    } else {
      const customerParams: ICustomerParams = this.model.shipToAccount!;

      shipToDetails += !!customerParams.name1.trim() ? `${customerParams.name1}<br />` : '';
      shipToDetails += !!customerParams.name2.trim() ? `${customerParams.name2}<br />` : '';
      shipToDetails += !!customerParams.name3.trim() ? `${customerParams.name3}<br />` : '';
      shipToDetails += !!customerParams.address.trim() ? `${customerParams.address}<br />` : '';
      shipToDetails += `${customerParams.city} ${customerParams.state} ${customerParams.zipcode}`;
      shipToDetails += !!customerParams.phone.trim() ? `<br />${customerParams.phone}` : '';
      shipToDetails += !!customerParams.email.trim() ? `<br />${customerParams.email}` : '';

      // 2 spaces only
      if (shipToDetails === '  ') {
        shipToDetails = 'Not Specified';
      }
    }

    return shipToDetails;
  };

  fleetcrossModal = (partNum: string = this.model.lastStockingPartEntry, autoInquire: boolean = false) => {
    if (this.model.sysParams.fleetcross) {
      this.fleetcrossAutoInquire = autoInquire;
      this.fleetcrossSeed = partNum;
      this.showFleetcrossModal = true;
    } else {
      toastr.error('Error', 'Fleetcross Not Enabled');
    }
  };

  // Legacy key command support - initialized with CTRL+ALT (best support for Linux & Windows)
  keyboardInterface = (evt: KeyboardEvent) => {
    if (!['PIN1', 'PRO1'].includes(this.model.runMode)) {
      return;
    }

    if (evt.altKey && evt.ctrlKey) {
      (document.activeElement as any).blur();
      this.keyboardInterfaceActive = true;

      // clear legacy key mode automatically after 5 seconds
      // use window.setTimeout to prevent tsc from getting confused with node's setTimeout return type
      clearTimeout(this.keyboardInterfaceTimerHandle);
      this.keyboardInterfaceTimerHandle = window.setTimeout(() => (this.keyboardInterfaceActive = false), 5000);

      return;
    }

    if (this.keyboardInterfaceActive) {
      // PROCESS THE KEY COMMAND HERE
      console.warn(`SHORTCUT KEY ISSUED: ${evt.key}`);

      const isPIN1 = this.model.runMode === 'PIN1';
      const isPRO1 = this.model.runMode === 'PRO1';

      switch (evt.key) {
        case ',':
          this.expandAllAlternativesInputRef.checked = true;
          this.hideAdditionalDetailInputRef.checked = false;
          runInAction(() => {
            this.model.hideAdditionalDetail = false;
            this.model.expandAllAlternatives = true;
          });
          evt.preventDefault();
          break;
        case ';':
          this.resetScreen();
          evt.preventDefault();
          break;
        case '/':
          if (this.model.ppcValidated) {
            this.resetPPC();
          } else {
            this.ppcAuthenticatorRef.focus();
          }
          evt.preventDefault();
          break;
        case '-':
          this.model.processByBinLocation = false;
          if (isPIN1) {
            this.process('pickticket');
          } else if (isPRO1) {
            this.processRO('pickticket');
          }
          evt.preventDefault();
          break;
        case '=':
          this.model.processByBinLocation = true;
          if (isPIN1) {
            this.process('pickticket');
          } else if (isPRO1) {
            this.processRO('pickticket');
          }
          evt.preventDefault();
          break;
        case 'b':
        case 'B':
          if (this.model.activeCustomerID !== null) {
            this.showConfirmationStatusModal = true;
          }
          evt.preventDefault();
          break;
        case 'c':
        case 'C':
          if (isPIN1) {
            if (!this.model.sysParams.noForcedCredits) {
              this.process('manualcredit');
            }
            evt.preventDefault();
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'd':
        case 'D':
          if (this.model.activeCustomerID !== null) {
            this.backorderType = 'DAILY';
            this.showBackorderModal = true;
          }
          evt.preventDefault();
          break;
        case 'i':
        case 'I':
          if (isPRO1) {
            this.processRO('issue');
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'h':
        case 'H':
        case 'k':
        case 'K':
          if (isPIN1) {
            if (this.model.activeCustomerID !== null) {
              this.loadCustomerHistoryModal();
            }
            evt.preventDefault();
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'l':
        case 'L':
          if (isPIN1) {
            if (this.model.ppcValidated) {
              this.showLostSaleReasonCodeSelectionModal = true;
            } else {
              toastr.error('Error', 'PPC required.');
            }
            evt.preventDefault();
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'm':
        case 'M':
          this.loadSavedList('Bill of Materials');
          evt.preventDefault();
          break;
        case 'n':
        case 'N':
          if (this.model.activeCustomerID !== null) {
            this.backorderType = 'NOW';
            this.showBackorderModal = true;
          }
          evt.preventDefault();
          break;
        case 'o':
        case 'O':
          if (isPRO1) {
            if (this.model.activeRONumber !== null) {
              this.model.moveOperationsModeActive = true;
            }
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'p':
        case 'P':
          if (isPIN1) {
            this.model.processByBinLocation = false;
            this.process('invoice');
            evt.preventDefault();
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'q':
        case 'Q':
          if (isPIN1) {
            // In the future, we might want quote by bin location
            this.model.processByBinLocation = false;
            this.process('quote');
            evt.preventDefault();
          } else {
            this.keyboardInterfaceActive = false;
            return;
          }
          break;
        case 'x':
        case 'X':
          this.fleetcrossModal();
          break;
        default:
          this.keyboardInterfaceActive = false;
          return;
      }

      const keyDiv = document.getElementById('sk-keyboardInterfaceActiveKey') as HTMLElement;
      keyDiv.innerHTML = evt.key;
      keyDiv.style.opacity = '1';
      keyDiv.style.transition = 'opacity 0ms';

      setTimeout(() => {
        keyDiv.style.opacity = '0';
        keyDiv.style.transition = 'opacity 300ms';
      }, 1350);

      this.keyboardInterfaceActive = false;
    }
  };

  loadCustomer = async (customerID: string, reqCashSaleInfoOnCODAccountLoad = true) => {
    if (await this.model.loadCustomerParams(customerID)) {
      // enable "external" loadCustomer() on customerLookupRef (pick list loads, etc.)
      this.customerLookupRef.value = this.model.activeCustomerID!;

      // is this a cod account?
      if (this.model.customerParams.isCodAccount && reqCashSaleInfoOnCODAccountLoad) {
        this.model.showShipToOverrideModal = true;

        // non-cod account customer successfully loaded, check for confirmations
      } else {
        // Confirmations Ready means at least 1 part has been received and is ready to sell.
        // We don't show this on default COD accounts as it would be far too noisy all day.
        if (this.model.customerParams.confirmationsReady && !this.model.customerParams.isCodAccount) {
          this.showPendingConfirmationsModal = true;
        } else {
          this.lineItemEntryRef.focusPartEntry();
        }
      }
    } else {
      setTimeout(() => {
        // revert customer component input to previously loaded customer ID and focus
        if (this.model.activeCustomerID) {
          // set state of customer component to activeCustomerID
          this.customerLookupRef.value = this.model.activeCustomerID;
        }
      }, 0);
    }
  };

  // Customer history is only fetched on demand since it has the potential of being large
  loadCustomerHistoryModal = () => {
    this.model.loadCustomerHistory();
    this.showCustomerHistoryModal = true;
  };

  loadLoyaltyCustomerModal = () => {
    modal.open('loyaltyCustomerSelect', {
      hidePrint: true,
      headerClass: 'primary',
      title: 'Load / Create Loyalty Customer',
      size: 'lg',
      footer: (
        <>
          <Button
            onClick={() => {
              modal.open('loyaltyCustomerUpdate', {
                onCreate: (loyaltyCustomer: any) => {
                  // Assuming you're only calling onCreate here if the action was successful.

                  // data includes additional fields, but we'll match a typical existing customer load
                  this.model.loadLoyaltyCustomer(loyaltyCustomer.data);

                  // close create modal + load/create loyalty customer modal
                  modal.close();
                  modal.close();
                },
                initialValues: {
                  customerNum: this.model.activeCustomerID,
                },
                // customerID in the existing props is for updating an existing loyalty customer ID
                // The following is an example prop to seed the dealer "Customer" field w/ on create
                // dealerCustomerNum={this.model.activeCustomerID || ''}
              });
            }}
            variant="success"
            style={{ float: 'left' }}
          >
            Create Customer
          </Button>
          <Button onClick={() => modal.close()} variant="secondary">
            Cancel
          </Button>
        </>
      ),
      message: (
        // @ts-ignore: who cares
        <Loyalty>
          <CustomerSelect
            dealerCustomerNum={this.model.activeCustomerID || ''}
            onSelect={(loyaltyCustomer: any) => {
              // Assuming you're only calling onSelect here if the action was successful.

              this.model.loadLoyaltyCustomer(loyaltyCustomer);
              modal.close();
            }}
          />
        </Loyalty>
      ),
    });
  };

  loadLoyaltyOfferModal = () => {
    const candidateLineItems = this.model.getLoyaltyCandidateLineItems();

    if (candidateLineItems === null) {
      // model specific error will be displayed in model code
      return;
    } else {
      if (candidateLineItems.length === 0) {
        toastr.error(
          'No Parts',
          `No ${
            ['PRO1'].includes(this.model.runMode) ? 'queued to issue ' : ''
          }stocking parts to check for coupon offers.`
        );
        return;
      } else {
        const initialOffersSelected = [...new Set(_.flatten(candidateLineItems.map(o => o.loyaltyOfferIDs)))]; // unique set
        const partNumbers = candidateLineItems.map(o => o.partNumber).join(',');

        modal.open('loyaltyOfferSelect', {
          hidePrint: true,
          hideFooter: true,
          message: (
            // @ts-ignore: who cares
            <Loyalty>
              <OfferPartList
                initialCustomerId={this.model.activeLoyaltyCustomerID!}
                initialOffersSelected={initialOffersSelected}
                initialSearch={{ externalCustomerId: this.model.activeLoyaltyCustomerID, partNumbers }}
                onSelect={(loyaltyOffers: ILoyaltyOffer[]) => {
                  // Match offers to parts, update main invoice screen
                  this.model.pairOffersToLoyaltyCandidateLineItems(candidateLineItems, loyaltyOffers);
                  modal.close();
                }}
              />
            </Loyalty>
          ),
        });
      }
    }
  };

  loadSavedList = (listType: SavedListType = 'Pick List', id: string = '') => {
    if (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode)) {
      // We allow picklist / bill of materials  lookup without first loading a RO, but if a RO is loaded,
      // there must be an active operation chosen.
      if (this.model.activeRONumber !== null) {
        if (this.model.activeOperation === 0) {
          toastr.error('Error', 'Please select an operation to issue to first.');
          return;
        }
        const locked = this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].locked;
        const voided = this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].voided;

        if (this.model.activeRONumber !== null && this.model.activeOperation !== 0 && (locked || voided)) {
          toastr.error('Error', `Operation ${locked ? 'Locked!' : 'Voided!'}`);
          return;
        }
      } // else just doing a PRO1 fast inquiry (for checking quantity, etc.)
    }

    this.savedListType = listType;
    this.savedListIDSeed = id;
    this.showLoadSavedListModal = true;
    setTimeout(() => {
      if (this.savedListIDInputRef) {
        this.savedListIDInputRef.focus();
      }
    }, 100);
  };

  onPPCValidated = (seedRun = false) => {
    switch (this.model.runMode) {
      case 'PIN1':
        if (this.customerLookupRef) {
          // If the customer is already loaded, we're only changing the PPC, next focus is part entry
          if (this.model.activeCustomerID) {
            this.lineItemEntryRef.focusPartEntry();
          } else {
            if (!seedRun) {
              this.customerLookupRef.focus();
            }
          }
        }
        break;
      case 'PRO1':
      case 'SMM3':
      case 'SRE2':
        // TODO: // If the RO number is already loaded, we're only changing the PPC, next focus is part entry
        this.roNumberInputRef.focus();
        break;
      case 'PIN4':
      case 'PSO3':
        this.lineItemEntryRef.focusPartEntry();
        break;
      default:
        break;
    }
  };

  @action
  process = async (type: 'invoice' | 'manualcredit' | 'pickticket' | 'quote') => {
    if (this.model.lineItems.length === 0) {
      toastr.error('Error', 'No items to process.');
      return;
    }

    const res = await this.model.process({ type });

    /* res.success will only occur here if all required fields are specified by the 
       clerk for the process type, and no printer selection is required. Otherwise,
       the various printer selection modals make calls to model.process re-entrantly
       and handle ex-post success events such as screen resets for invoices.

       res.redirect will occur on process for all types if required fields are missing,
       before printer selection is considered.
    */
    if (res.success) {
      this.resetScreen();
    } else {
      switch (res.redirect) {
        case 'CUST':
          this.customerLookupRef.focus();
          break;
        case 'FREIGHT':
          this.freightInputRef.focus();
          break;
        case 'FUELCHARGE':
          this.fuelChargeInputRef.focus();
          break;
        case 'PPC':
          this.ppcAuthenticatorRef.focus();
          break;
        case 'PO':
          this.poInputRef.focus();
          break;
        case 'RECEIVEDBY':
          this.receivedByInputRef.focus();
          break;
        case 'TRANSFERRO':
          this.transferRoNumInputRef.focus();
          break;
        case 'TRANSFERCONDITION':
          this.transferConditionNumInputRef.focus();
          break;
        case 'ROUTE':
        default:
          // NO-OP
          break;
      }
    }
  };

  @action
  processRO = (type: 'issue' | 'decisivissue' | 'negativeissue' | 'pickticket' | 'decisivpickticket') => {
    if (!this.model.lineItems.some(o => !o.issuedToRO)) {
      toastr.error('Error', 'No items to process.');
      return;
    }

    this.model.processRO({ type });
  };

  // Note this simply clears all line items and sets focus back to the
  // part entry input, it does NOT clear the loaded PPC or active customer
  resetInvoiceBuilder = () => {
    this.model.removeAllLineItems();
    this.lineItemEntryRef.focusPartEntry();
  };

  // Clears the PPC and disables customer search until a valid PPC is entered.
  resetPPC = () => {
    this.model.clearPPC();
    this.ppcAuthenticatorRef.focus();
  };

  // Reset all components of the UI
  resetScreen = () => {
    // pending actions? could be hung D3/QM calls, license exhaustion, etc.
    if (this.model.isLineFetching() || this.model.customerLoadInProcess || this.model.roLoadInProcess) {
      window.location.reload();
      return;
    }

    this.model.reset(); // render (brings PPC input back)

    switch (this.model.runMode) {
      case 'PIN1':
        this.customerLookupRef.clear();
        break;
      case 'PRO1':
      case 'SMM3':
      case 'SRE2':
        this.roNumberInputRef.value = '';
        break;
      default:
        break;
    }

    this.lineItemEntryRef.clear();
    this.initialUIRender = true; // re-init PPC controls & focus
    this.forceUpdate(); // 2nd render

    // freight and po inputs are removed by render() on reset
    // fresh refs are mapped after the next customer is loaded
  };

  selectFreight = () => {
    this.freightInputRef.focus();
    this.freightInputRef.setSelectionRange(0, this.freightInputRef.value.length);
  };

  selectFuel = () => {
    this.fuelChargeInputRef.focus();
    this.fuelChargeInputRef.setSelectionRange(0, this.fuelChargeInputRef.value.length);
  };

  render(): JSX.Element {
    // Beta lock check
    if (this.betaLock) {
      return <FileNotFound pageName={this.betaPage} terminal={true} />;
    }

    if (this.model.runMode === 'INVALID') {
      return <FileNotFound />;
    }

    // Wait for all SystemParameters to load
    if (!this.model.initialized) {
      return <LoadingPage />;
    }

    const isPIN1 = this.model.runMode === 'PIN1';
    const isPRO1 = this.model.runMode === 'PRO1';

    // Popover to list available keyboard shortcuts
    const commandKeyPopover = (
      <Popover id="modal-popover">
        <Popover.Title>Keyboard Shortcuts</Popover.Title>
        <Popover.Content>
          <strong>,</strong> &nbsp;Expand All Alternatives
          <br />
          <strong>;</strong> &nbsp;Reset Screen
          <br />
          <strong>/</strong> &nbsp;Reset PPC
          <br />
          <strong>-</strong> &nbsp;Process Pick Ticket
          <br />
          <strong>=</strong> &nbsp;Process Pick Ticket by Bin Location
          <br />
          <strong>b</strong> &nbsp;Backorder Status
          <br />
          {!this.model.sysParams.noForcedCredits && isPIN1 && (
            <>
              <strong>c</strong> &nbsp;Manual Credit
              <br />
            </>
          )}
          <strong>d</strong> &nbsp;Daily Backorder
          <br />
          {isPIN1 && (
            <>
              <strong>h</strong> &nbsp;Invoice History
              <br />
              <strong>k</strong> &nbsp;Core Bank
              <br />
            </>
          )}
          {isPRO1 && (
            <>
              <strong>i</strong> &nbsp; Issue
              <br />
            </>
          )}
          {isPIN1 && (
            <>
              <strong>l</strong> &nbsp; Record Lost Sale
              <br />
            </>
          )}
          <strong>m</strong> &nbsp;Bill of Materials
          <br />
          <strong>n</strong> &nbsp;Now/Emergency Backorder
          <br />
          {isPRO1 && (
            <>
              <strong>o</strong> &nbsp; Move Operations
              <br />
            </>
          )}
          {isPIN1 && (
            <>
              <strong>p</strong> &nbsp;Process Invoice
              <br />
              <strong>q</strong> &nbsp;Process Quote
              <br />
            </>
          )}
          <strong>x</strong> &nbsp;Fleetcross Lookup
          <br />
        </Popover.Content>
      </Popover>
    );

    // We use various UI color themes to indicate CASH, CREDIT OVER, etc.
    const containerStyle: { backgroundColor?: string } = {};
    const creditMsgStyle: { color?: string } = {};

    // Customer loaded?  Use interface colors to indicate their CREDIT/CASH status.
    if (this.model.runMode === 'PIN1' && this.model.activeCustomerID) {
      if (this.model.customerParams.customerCashOnly || this.model.forceCashSale) {
        containerStyle.backgroundColor = '#CFC'; // light CASH green
        creditMsgStyle.color = '#439A46';
      } else if (this.model.customerParams.customerOverLimit || this.model.totalOverCreditLimit) {
        containerStyle.backgroundColor = '#FCC'; // light red - over limit
        creditMsgStyle.color = '#CB171E';
      }
    }

    // See shopkeeper.scss for flex-based layout strategy
    return (
      <div id="shopkeeper" style={containerStyle}>
        {this.devtools && <Devtools />}

        {/*PPC, Credit Message, Balance Due*/}
        <div className="sk-mainUI">
          <div className="sk-ppcCreditMsgBalance">
            <PPCAuthenticator
              model={this.model}
              onPPCValidated={this.onPPCValidated}
              resetPPC={this.resetPPC}
              skipPPC={() => this.lineItemEntryRef.focusPartEntry()}
              ref={(ref: PPCAuthenticator) => (this.ppcAuthenticatorRef = ref)}
            />

            {this.model.runMode === 'PIN1' && (
              <div className="sk-creditMsg" style={creditMsgStyle}>
                {this.model.activeCustomerID &&
                  (this.model.customerParams.customerCashOnly || this.model.forceCashSale ? (
                    <div>
                      <div>*** CASH REQUIRED ***</div>
                      {['PIN1'].includes(this.model.runMode) && this.model.sysParams.creditCardIntegration && (
                        <div className="sk-creditCardSale">
                          {/* It's easiest to read this value from the ref on invoice process since loaded */}
                          {/* customer changes as well as variance changes would require model tracking */}
                          {/* and lead to a more complex solution. */}
                          <FormCheck inline={true}>
                            <FormCheck.Input
                              type="checkbox"
                              id="creditCardSale"
                              className="sk-useMaterialInput ml-1"
                              tabIndex={-1}
                              ref={(ref: HTMLInputElement) => (this.creditCardSaleInputRef = ref)}
                            />
                            <FormCheck.Label htmlFor="creditCardSale">
                              <i className="fad fa-credit-card" /> Pay with Credit Card
                              {this.model.customerParams.ccOnFile > 0
                                ? this.model.customerParams.ccOnFile > 1
                                  ? ' (Cards Exist on File)'
                                  : ' (Card Exists on File)'
                                : ''}
                            </FormCheck.Label>
                          </FormCheck>
                        </div>
                      )}
                    </div>
                  ) : this.model.customerParams.customerOverLimit ? (
                    '*** OVER LIMIT ***'
                  ) : (
                    this.model.customerParams.availableCreditMsg || '*** CHARGE ***'
                  ))}
                {this.model.activeCustomerID && this.model.OPCtag && (
                  <h6 style={{ marginTop: '3px' }}>
                    <Badge variant="primary">{this.model.OPCtag}</Badge>
                  </h6>
                )}
              </div>
            )}

            {this.model.runMode === 'PSO3' && (
              <div className="sk-balanceDue">
                Total Cost:
                <br />
                <span className="sk-amount">
                  <span className={this.model.totalCost.calcInProcess ? 'sk-calcInProgress' : ''}>
                    ${this.model.totalCost.amount}
                  </span>
                </span>
              </div>
            )}

            {this.model.runMode === 'PIN4' && (
              <div className="sk-balanceDue">
                Total:
                <br />
                <span className="sk-amount">
                  <span className={this.model.totalInvoice.calcInProcess ? 'sk-calcInProgress' : ''}>
                    ${this.model.totalInvoice.amount}
                  </span>
                </span>
              </div>
            )}

            {['PIN1', 'PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
              <div className="sk-balanceDue">
                Balance Due:
                <br />
                <span className="sk-amount">
                  <span className={this.model.totalInvoice.calcInProcess ? 'sk-calcInProgress' : ''}>
                    ${this.model.totalInvoice.amount}
                  </span>
                </span>
                {/* In the event of an API error during tax calc, give the user a way to manually try again. */}
                {this.model.showManualTaxCalc && (
                  <div className="sk-actionLink" onClick={() => this.model.updateTaxes()}>
                    Retry Tax Calculation
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="sk-orderInfo">
            <div className="sk-orderInfoLeftContainer">
              {/* Begin PIN1 Specific Controls */}
              {['PIN1'].includes(this.model.runMode) && (
                <>
                  <div className="sk-custNo">
                    <Label>Customer Number</Label>
                    <CustomerLookup
                      activeId={this.model.activeCustomerID}
                      disabled={!this.model.ppcValidated || this.model.runMode !== 'PIN1'}
                      hideInactiveAccounts={this.model.sysParams.hideInactiveAccounts}
                      hideInvoiceDisabledAccounts={true}
                      id="mainCustomerLookup"
                      onCustomerLoad={this.loadCustomer}
                      onEnterKeyPressed={() => this.lineItemEntryRef.focusPartEntry()}
                      onPickListLoad={(id: string) => this.loadSavedList('Pick List', id)}
                      showAdditionalNames={this.model.sysParams.showName23}
                      style={{ width: '13em' }}
                      ref={(ref: CustomerLookup) => (this.customerLookupRef = ref)}
                    />
                    {this.model.activeCustomerID && (
                      <div
                        className="sk-actionLink"
                        onClick={() => {
                          this.showCustomerVehiclesModal = true;
                        }}
                        style={{ marginLeft: '3px', marginTop: '2px' }}
                      >
                        <i className="zmdi zmdi-truck" /> Vehicles
                      </div>
                    )}
                  </div>

                  {this.model.activeCustomerID && (
                    <>
                      <div className="sk-shipTo">
                        <Label>Customer Address</Label>
                        <div
                          className="sk-shipToDetails"
                          dangerouslySetInnerHTML={{ __html: this.displayShipTo() }}
                          onClick={() => {
                            if (this.model.ppcValidated) {
                              this.model.showShipToOverrideModal = true;
                            }
                          }}
                        />
                        <div
                          className="sk-actionLink"
                          onClick={() => {
                            this.showEmailOptionsModal = true;
                          }}
                          style={{ marginLeft: '3px' }}
                        >
                          <i className="zmdi zmdi-email" /> Email Options
                        </div>
                      </div>
                      {(this.model.shipToAccount || this.model.shipToAddress) && (
                        <div className="sk-shipTo">
                          <Label>Ship To</Label>
                          <div
                            className="sk-shipToDetails"
                            dangerouslySetInnerHTML={{ __html: this.displayShipToOverride() }}
                            onClick={() => (this.model.showShipToOverrideModal = true)}
                          />
                          <div
                            className="sk-actionLink"
                            style={{ textAlign: 'center' }}
                            onClick={() => {
                              this.model.clearShipToOverride();
                              this.lineItemEntryRef.focusPartEntry();
                            }}
                          >
                            Clear Ship To Override
                          </div>
                        </div>
                      )}
                      {this.model.activeLoyaltyCustomerID && (
                        <div className="sk-shipTo">
                          <Label>PACCAR Loyalty</Label>
                          <div
                            className="sk-shipToDetails"
                            dangerouslySetInnerHTML={{ __html: this.displayLoyaltyCustomer() }}
                            onClick={() => {
                              modal.open('loyaltyCustomerUpdate', {
                                customerId: this.model.activeLoyaltyCustomerID,
                                onUpdate: (loyaltyCustomer: any) => {
                                  // data includes additional fields, but we'll match a typical existing customer load
                                  this.model.loadLoyaltyCustomer(loyaltyCustomer.data);
                                  modal.close();
                                },
                              });
                            }}
                          />
                          <div
                            className="sk-actionLink"
                            style={{ textAlign: 'center' }}
                            onClick={() => {
                              this.model.clearLoyalty();
                              this.lineItemEntryRef.focusPartEntry();
                            }}
                          >
                            Clear Loyalty Customer
                          </div>
                        </div>
                      )}
                      <div className="sk-POFreight">
                        <div>
                          <Label>
                            P.O. #{' '}
                            {this.model.sysParams.showPurchaseOrderPattern &&
                              !this.model.suspendPORules &&
                              this.model.customerParams.purchaseOrderPatternReq}
                          </Label>
                          {this.model.customerParams.purchaseOrderBlanketPO && !this.model.suspendPORules && (
                            <div className="sk-blanketPODisplay">
                              {this.model.customerParams.purchaseOrderBlanketPO}
                            </div>
                          )}
                          <input
                            type="text"
                            onBlur={() => {
                              const po = this.poInputRef.value.trim();

                              // Request to load a quote for this customer using the "legacy" way
                              if (po.toUpperCase().startsWith('QOT')) {
                                this.poInputRef.value = '';
                                this.lineItemEntryRef.focusPartEntry();
                                this.loadSavedList('Quote', po);

                                // If PO was previously set, clear it.
                              } else if (po === '') {
                                this.model.setPo(null);

                                // Otherwise attempt to set the PO in the model to the user's specification.
                              } else {
                                this.model.setPo(po);
                              }
                            }}
                            onKeyPress={evt => {
                              if (evt.key === 'Enter') {
                                // The following will trigger onBlur logic
                                this.freightInputRef.focus();
                              }
                            }}
                            ref={(ref: HTMLInputElement) => (this.poInputRef = ref)}
                            className={
                              this.model.customerParams.purchaseOrderBlanketPO && !this.model.suspendPORules
                                ? 'sk-blanketPOActive'
                                : ''
                            }
                          />
                        </div>
                        <div>
                          <Label>Freight</Label>
                          <input
                            defaultValue={this.model.freight !== null ? this.model.freight.toFixed(2) : ''}
                            type="text"
                            onBlur={() => {
                              let freight: string | number = this.freightInputRef.value.trim();

                              // Possible request to clear a previously set freight
                              if (freight === '') {
                                this.model.setFreight(null);

                                // Otherwise attempt to parse the freight / massage to proper format.
                              } else {
                                freight = parseFloat(this.freightInputRef.value);
                                if (!isNaN(freight)) {
                                  // Insure freight is x.xx format in UI and rounded for model.
                                  this.freightInputRef.value = freight.toFixed(2);
                                  freight = parseFloat(this.freightInputRef.value);
                                  this.model.setFreight(freight);

                                  // Otherwise the freight as entered is not a parsable number
                                } else {
                                  toastr.error('Error', 'Freight Invalid Format', { toastId: 'freightInput' });
                                }
                              }
                            }}
                            onKeyPress={evt => {
                              if (evt.key === 'Enter') {
                                // The following will trigger onBlur logic
                                this.receivedByInputRef.focus();
                              }
                            }}
                            ref={(ref: HTMLInputElement) => (this.freightInputRef = ref)}
                          />
                        </div>
                      </div>
                      <div className="sk-receivedByRouteMethod">
                        <div>
                          <Label>Received By</Label>
                          <input
                            maxLength={27}
                            type="text"
                            onBlur={() => {
                              const receivedBy: string = this.receivedByInputRef.value.trim();
                              this.model.setReceivedBy(receivedBy === '' ? null : receivedBy);
                            }}
                            onKeyPress={evt => {
                              if (evt.key === 'Enter') {
                                // The following will trigger onBlur logic
                                this.lineItemEntryRef.focusPartEntry();
                              }
                            }}
                            ref={(ref: HTMLInputElement) => (this.receivedByInputRef = ref)}
                          />
                        </div>
                        <div className="sk-routeMethod">
                          <Label>Route</Label>
                          <DropdownButton
                            title={_.get(
                              _.find(this.model.sysParams.shippingCodes, {
                                code: this.model.activeRoutingMethod as string,
                              }),
                              'desc',
                              'Select Route'
                            )}
                            id="shippingMethod"
                            variant="secondary"
                          >
                            {this.model.sysParams.shippingCodes.map((method, index) => {
                              return (
                                <Dropdown.Item
                                  active={method.code === this.model.activeRoutingMethod}
                                  eventKey={String(index)}
                                  key={index}
                                  onClick={() => this.model.setRoutingMethod(method.code)}
                                >
                                  {method.desc}
                                </Dropdown.Item>
                              );
                            })}
                          </DropdownButton>
                        </div>
                      </div>
                      {this.model.promptForFuel && (
                        <div className="sk-fuelCharge">
                          <div style={{ visibility: 'hidden' }}>
                            <Label>Unused</Label>
                            <input
                              type="text"
                              onBlur={() => {
                                /* Unused block for alignment / future use */
                              }}
                            />
                          </div>
                          <div>
                            <Label>Fuel Charge</Label>
                            <input
                              defaultValue={this.model.fuelCharge !== null ? this.model.fuelCharge.toFixed(2) : ''}
                              type="text"
                              onBlur={() => {
                                let fuelCharge: string | number = this.fuelChargeInputRef.value.trim();

                                // Possible request to clear a previously set fuel charge
                                if (fuelCharge === '') {
                                  this.model.setFuelCharge(null);

                                  // Otherwise attempt to parse the fuel charge / massage to proper format.
                                } else {
                                  fuelCharge = parseFloat(this.fuelChargeInputRef.value);
                                  if (!isNaN(fuelCharge)) {
                                    // Insure fuel charge is x.xx format in UI and rounded for model.
                                    this.fuelChargeInputRef.value = fuelCharge.toFixed(2);
                                    fuelCharge = parseFloat(this.fuelChargeInputRef.value);
                                    this.model.setFuelCharge(fuelCharge);

                                    // Otherwise the fuel charge as entered is not a parsable number
                                  } else {
                                    toastr.error('Error', 'Fuel Charge Invalid Format', { toastId: 'fuelChargeInput' });
                                  }
                                }
                              }}
                              onKeyPress={evt => {
                                if (evt.key === 'Enter') {
                                  // The following will trigger onBlur logic
                                  this.lineItemEntryRef.focusPartEntry();
                                }
                              }}
                              ref={(ref: HTMLInputElement) => (this.fuelChargeInputRef = ref)}
                            />
                          </div>
                        </div>
                      )}
                      {this.model.isROTransferCustomerLoaded ? (
                        <div className="sk-roTransfer">
                          <div>
                            <Label>RO Number</Label>
                            <input
                              type="text"
                              onBlur={() => {
                                const transferRoNum: string = this.transferRoNumInputRef.value.trim();
                                this.model.setTransferRoNum(transferRoNum === '' ? null : transferRoNum);
                              }}
                              onKeyPress={evt => {
                                if (evt.key === 'Enter') {
                                  // The following will trigger onBlur logic
                                  this.transferConditionNumInputRef.focus();
                                }
                              }}
                              ref={(ref: HTMLInputElement) => (this.transferRoNumInputRef = ref)}
                            />
                          </div>
                          <div>
                            <Label>Condition Number</Label>
                            <input
                              type="text"
                              onBlur={() => {
                                const transferConditionNum: string = this.transferConditionNumInputRef.value.trim();
                                this.model.setTransferConditionNum(
                                  transferConditionNum === '' ? null : transferConditionNum
                                );
                              }}
                              onKeyPress={evt => {
                                if (evt.key === 'Enter') {
                                  // The following will trigger onBlur logic
                                  this.lineItemEntryRef.focusPartEntry();
                                }
                              }}
                              ref={(ref: HTMLInputElement) => (this.transferConditionNumInputRef = ref)}
                            />
                          </div>
                        </div>
                      ) : this.model.isLedgerDetailRequired ? (
                        <div className="sk-glDetail">
                          <div>
                            <Label>GL Number</Label>
                            <input
                              type="text"
                              disabled={!!this.model.customerParams.internalCustomerGLAccountWithControlRequired}
                              onBlur={() => {
                                const glNum: string = this.glNumInputRef.value.trim().toUpperCase();
                                this.model.setGlNum(glNum === '' ? null : glNum);
                              }}
                              onKeyPress={evt => {
                                if (evt.key === 'Enter') {
                                  // The following will trigger onBlur logic
                                  this.controlNumInputRef.focus();
                                }
                              }}
                              ref={(ref: HTMLInputElement) => (this.glNumInputRef = ref)}
                              defaultValue={this.model.customerParams.internalCustomerGLAccountWithControlRequired}
                            />
                          </div>
                          <div>
                            <Label>
                              {this.model.customerParams.internalCustomer
                                ? 'Stock'
                                : this.model.customerParams.warrantyCustomer
                                ? 'Claim'
                                : 'Control'}{' '}
                              Number{this.model.controlNumRequired ? ' (Required)' : ''}
                            </Label>
                            <input
                              type="text"
                              onBlur={() => {
                                const controlNum: string = this.controlNumInputRef.value.trim();
                                this.model.setControlNum(controlNum === '' ? null : controlNum);
                              }}
                              onKeyPress={evt => {
                                if (evt.key === 'Enter') {
                                  // The following will trigger onBlur logic
                                  this.lineItemEntryRef.focusPartEntry();
                                }
                              }}
                              ref={(ref: HTMLInputElement) => (this.controlNumInputRef = ref)}
                            />
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
              {/* End PIN1 Specific Controls */}

              {/* Begin PRO1/SMM3 Specific Controls */}
              {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
                <>
                  <div className="sk-roNo">
                    <Label>
                      {this.model.runMode === 'PRO1'
                        ? 'RO / Estimate'
                        : this.model.runMode === 'SRE2'
                        ? 'Estimate'
                        : 'RO'}{' '}
                      Number
                    </Label>
                    <input
                      type="text"
                      disabled={!this.model.ppcValidated || this.model.roLoadInProcess}
                      onBlur={async () => {
                        const roNumber = this.roNumberInputRef.value.trim();
                        const prefix = roNumber.substr(0, 3).toUpperCase();

                        if (!!roNumber) {
                          // SRE2 is ESTxxx only
                          if (this.model.runMode === 'SRE2') {
                            if (prefix !== 'EST' && !prefix.startsWith('-')) {
                              toastr.error('Error', 'Estimates only (ESTxxx).');
                              return;
                            }
                          }

                          if (this.model.runMode === 'SMM3') {
                            if (prefix === 'EST') {
                              toastr.error('Error', 'Estimates available only in SRE2 or PRO1.');
                              return;
                            }

                            if (roNumber.startsWith('-')) {
                              toastr.error('Error', 'Pick lists not supported in SMM3.');
                              return;
                            }
                          }

                          this.model.loadRepairOrder({ roNumber, includeAttachments: true }).then(success => {
                            if (!success) {
                              // If we currently have a loaded RO, and the newly entered RO isn't loadable, revert RO # for screen accuracy
                              if (!!this.model.activeRONumber) {
                                this.roNumberInputRef.value = this.model.activeRONumber;
                              }
                            } else {
                              if (
                                this.model.activeRepairOrderDetails.confirmationsReady &&
                                !['SMM3', 'SRE2'].includes(this.model.runMode) &&
                                !this.model.isEstimate
                              ) {
                                this.showPendingConfirmationsModal = true;
                              }
                            }
                          });
                        } else {
                          // If user tried to clear the field and a RO is currently loaded, revert it (they should RESET instead)
                          if (!!this.model.activeRONumber) {
                            this.roNumberInputRef.value = this.model.activeRONumber;
                          }
                        }
                      }}
                      onKeyPress={evt => {
                        if (evt.key === 'Enter') {
                          this.roNumberInputRef.blur();
                          this.lineItemEntryRef.focusPartEntry();
                        }
                      }}
                      ref={(ref: HTMLInputElement) => (this.roNumberInputRef = ref)}
                    />
                    {this.model.activeRONumber && (
                      <div
                        className="sk-refreshROData"
                        title={`Refresh ${this.model.isEstimate ? 'Estimate' : 'Repair Order'} Data`}
                        onClick={() => {
                          this.model
                            .refreshActiveROData({ retainIssueQueue: 'ALLOPS', includeAttachments: true })
                            .then(() => toastr.success('Refreshed', 'Data Refreshed'));
                        }}
                      >
                        <i className="fas fa-sync-alt" /> Refresh {this.model.isEstimate ? 'Estimate' : 'RO'} Data
                      </div>
                    )}
                  </div>
                  {this.model.activeCustomerID && (
                    <div className="sk-roInfoContainer">
                      <Label>Customer</Label>
                      <div className="sk-roInfoDetails" dangerouslySetInnerHTML={{ __html: this.displayShipTo() }} />
                    </div>
                  )}
                  {this.model.activeLoyaltyCustomerID && (
                    <div className="sk-shipTo">
                      <Label>PACCAR Loyalty</Label>
                      <div
                        className="sk-shipToDetails"
                        dangerouslySetInnerHTML={{ __html: this.displayLoyaltyCustomer() }}
                      />
                    </div>
                  )}
                  {this.model.activeRONumber && (
                    <div className="sk-roInfoContainer">
                      <Label>Serial #</Label>
                      <div className="sk-roInfoDetails">{this.model.activeRepairOrderDetails.vin || 'N/A'}</div>
                      <Label>Unit #</Label>
                      <div className="sk-roInfoDetails">{this.model.activeRepairOrderDetails.unit || 'N/A'}</div>
                      {this.model.activeRepairOrderDetails.vin && (
                        <div className="sk-unitInfoDrilldowns">
                          <ButtonGroup>
                            <button
                              className="btn btn-secondary btn-xs"
                              title="History"
                              onClick={() =>
                                modal.open('vehicleHistory', {
                                  lookupKey: this.model.activeRepairOrderDetails.vin,
                                })
                              }
                            >
                              <i className="fad fa-history fa-fw" />
                            </button>
                            <button
                              className="btn btn-secondary btn-xs"
                              title="Part History"
                              onClick={() =>
                                modal.open('vehiclePartHistory', {
                                  lookupKey: this.model.activeRepairOrderDetails.vin,
                                })
                              }
                            >
                              <i className="fad fa-toolbox fa-fw" />
                            </button>
                            <button
                              className="btn btn-secondary btn-xs"
                              title="Details"
                              onClick={() =>
                                modal.open('vehicleInformation', {
                                  lookupKey: this.model.activeRepairOrderDetails.vin,
                                })
                              }
                            >
                              <i className="fad fa-truck-container fa-fw" />
                            </button>
                            <button
                              className="btn btn-secondary btn-xs"
                              title="Attachments"
                              onClick={() =>
                                modal.open('attachments', {
                                  itemId: this.model.activeRepairOrderDetails.vin,
                                  itemName: 'SUF2',
                                  readOnly: true,
                                })
                              }
                            >
                              <i className="fad fa-paperclip fa-fw" />
                            </button>
                          </ButtonGroup>
                        </div>
                      )}
                    </div>
                  )}

                  {this.model.activeRONumber && this.model.activeRepairOrderDetails.unitData.length > 0 && (
                    <div className="sk-roInfoContainer">
                      <Label>Unit Information</Label>
                      <div className="sk-unitInformation">
                        {this.model.activeRepairOrderDetails.unitData.map((data, idx) => {
                          // "QMDATA": "COMES LIKE THIS"
                          const field = data.field.trim().replace(/(^")|("$)/g, '');
                          const value = data.value.trim().replace(/(^")|("$)/g, '');

                          return (
                            <div key={`unitData${idx}`}>
                              <span className="sk-unitInformationField">{field}:</span> {value}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {this.model.activeRONumber &&
                    (this.model.roAttachments.length > 0 || this.model.unitAttachments.length > 0) && (
                      <div className="sk-roInfoContainer">
                        <Tabs className="sk-serviceAttachmentsTabs">
                          {this.model.roAttachments.length > 0 && (
                            <Tab eventKey="ro" title={`${this.model.isEstimate ? 'EST' : 'RO'} Attachments`}>
                              <div
                                className="sk-serviceAttachments"
                                onClick={() =>
                                  modal.open('attachments', {
                                    itemId: this.model.activeRONumber,
                                    itemName: 'SMM1',
                                    readOnly: true,
                                  })
                                }
                              >
                                {this.model.roAttachments.map((o, i) => (
                                  <div className="sk-thumbnail-wrapper" key={`thumb-${i}`}>
                                    <img src={`${config.serverUrl}/api/attachment/${o.key}/thumbnail?resize=80px`} />
                                  </div>
                                ))}
                              </div>
                            </Tab>
                          )}
                          {this.model.unitAttachments.length > 0 && (
                            <Tab eventKey="unit" title="Unit Attachments">
                              <div
                                className="sk-serviceAttachments"
                                onClick={() =>
                                  modal.open('attachments', {
                                    itemId: this.model.activeRepairOrderDetails.vin,
                                    itemName: 'SUF2',
                                    readOnly: true,
                                  })
                                }
                              >
                                {this.model.unitAttachments.map((o, i) => (
                                  <div className="sk-thumbnail-wrapper" key={`thumb-${i}`}>
                                    <img src={`${config.serverUrl}/api/attachment/${o.key}/thumbnail?resize=80px`} />
                                  </div>
                                ))}
                              </div>
                            </Tab>
                          )}
                        </Tabs>
                      </div>
                    )}
                </>
              )}
              {/* End PRO1/SMM3 Specific Controls */}
            </div>

            {/* Begin Customer Notes / Active Operation */}

            {['PIN1'].includes(this.model.runMode) &&
              this.model.activeCustomerID &&
              (this.model.customerParams.memoOrderScreen.length || this.model.customerParams.purchaseOrderRequired) && (
                <div className="sk-orderInfoRightContainer">
                  <Label>Notes</Label>
                  {this.model.customerParams.memoOrderScreen.length > 0 && (
                    <div className="sk-customerMemo" dangerouslySetInnerHTML={{ __html: this.displayMemo() }} />
                  )}
                  {this.model.customerParams.purchaseOrderRequired && (
                    <div className="sk-poRequired">CUST PO REQUIRED</div>
                  )}
                </div>
              )}

            {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && this.model.activeRONumber && (
              <div className="sk-orderInfoRightContainer">
                <div
                  className={classNames({
                    'sk-activeOperation': true,
                    'sk-activeOperationLocked':
                      this.model.activeOperation !== 0 &&
                      (this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].locked ||
                        this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].voided),
                  })}
                  onClick={() => (this.showOperationSelectionModal = true)}
                >
                  <div className="sk-activeOperationLabel">Operation</div>
                  <div className="sk-activeOperationNumber">
                    {this.model.activeOperation !== 0 &&
                      this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].locked && (
                        <span>
                          <i className="zmdi zmdi-lock" />{' '}
                        </span>
                      )}
                    {this.model.activeOperation === 0 ? 'ALL' : this.model.activeOperation}
                  </div>
                  {this.model.activeOperation !== 0 && (
                    <div className="sk-activeLaborType">
                      Labor Type:{' '}
                      {this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].laborType}
                      {this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation]
                        .secondaryLaborType &&
                        `/${
                          this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation]
                            .secondaryLaborType
                        }`}
                    </div>
                  )}
                  {((this.model.activeOperation === 0 &&
                    this.model.activeRepairOrderDetails.operationDetails.some(o => o.warranty)) ||
                    (this.model.activeOperation !== 0 &&
                      this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].warranty)) && (
                    <div className="sk-warrantyBadge">
                      <Badge variant="primary">Warranty</Badge>
                    </div>
                  )}
                </div>
                {this.model.preIssueQueueLength > 0 && (
                  <div className="sk-issueQueue">Issue Queue: {this.model.preIssueQueueLength}</div>
                )}
              </div>
            )}
          </div>

          {/* The active mode dictates what to show in the main area of the screen. */}
          {this.activeMode === Mode.InvoiceBuilder && (
            <div className="sk-activeModeContainer">
              {/* Part Locator */}
              <LineItemEntry
                model={this.model}
                onBillOfMaterialsLoad={(id: string) => this.loadSavedList('Bill of Materials', id)}
                onFleetcrossInquiry={() => this.fleetcrossModal()}
                ref={(ref: LineItemEntry) => (this.lineItemEntryRef = ref)}
              />

              {/* Main Invoice Display */}
              <InvoiceBuilder
                model={this.model}
                editFreight={this.selectFreight}
                editFuel={this.selectFuel}
                fleetcrossModal={this.fleetcrossModal}
              />

              {/* Totals Div Below Main Invoice if line items have been added */}
              {this.model.lineItems.length > 0 && (
                <div className="sk-totalsDiv">
                  {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
                    <>
                      {this.model.activeOperation !== 0 && (
                        <div className="sk-totalParts">
                          Total Operation ({this.model.activeOperation}):&nbsp;
                          <span className={this.model.totalOperationParts.calcInProcess ? 'sk-calcInProgress' : ''}>
                            ${this.model.totalOperationParts.amount}
                          </span>
                        </div>
                      )}
                      <div className="sk-totalInvoice">
                        Total{' '}
                        {this.model.activeRONumber !== null
                          ? this.model.isEstimate
                            ? 'Estimate'
                            : 'Repair Order'
                          : 'Parts'}
                        :&nbsp;
                        <span className={this.model.totalParts.calcInProcess ? 'sk-calcInProgress' : ''}>
                          ${this.model.totalParts.amount}
                        </span>
                      </div>
                    </>
                  )}
                  {this.model.runMode === 'PIN1' && (
                    <>
                      <div className="sk-totalParts">
                        Total Parts:&nbsp;
                        <span className={this.model.totalParts.calcInProcess ? 'sk-calcInProgress' : ''}>
                          ${this.model.totalParts.amount}
                        </span>
                      </div>
                      <div className="sk-totalInvoice">
                        Total Invoice:&nbsp;
                        <span className={this.model.totalInvoice.calcInProcess ? 'sk-calcInProgress' : ''}>
                          ${this.model.totalInvoice.amount}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
              {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
                <div
                  className={`sk-moveSelectAll ${
                    this.model.moveOperationsModeActive && this.model.activeOperation !== 0 ? 'fadeIn' : 'out'
                  }`}
                >
                  <Button
                    variant="warning"
                    onClick={() => {
                      this.model.moveOperationsSelectAllOnActiveOperation();
                    }}
                  >
                    Select All Items on This Op
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* activeMode option: Past Invoice Dispaly (Credits, etc.) */}

          {/* activeMode option: Back Order Interface */}
        </div>

        {/* Ensure you leave enough margin bottom for the fixed footer actions bar. */}
        <div className="sk-bottomMainUI">
          <div className="sk-invoiceBuilderOptions">
            {!['SMM3'].includes(this.model.runMode) && (
              <FormCheck inline={true}>
                <FormCheck.Input
                  type="checkbox"
                  id="expandAllAlternatives"
                  className="sk-useMaterialInput ml-1"
                  onChange={(checkbox: any) => (this.model.expandAllAlternatives = checkbox.target.checked)}
                  tabIndex={-1}
                  ref={(ref: HTMLInputElement) => (this.expandAllAlternativesInputRef = ref)}
                />
                <FormCheck.Label htmlFor="expandAllAlternatives">Expand Alternatives</FormCheck.Label>
              </FormCheck>
            )}
            <FormCheck inline={true}>
              <FormCheck.Input
                checked={this.model.hideAdditionalDetail}
                type="checkbox"
                id="hideAdditionalDetail"
                className="sk-useMaterialInput ml-1"
                onChange={(checkbox: any) => (this.model.hideAdditionalDetail = checkbox.target.checked)}
                tabIndex={-1}
                ref={(ref: HTMLInputElement) => (this.hideAdditionalDetailInputRef = ref)}
              />
              <FormCheck.Label htmlFor="hideAdditionalDetail">Hide Extra Details</FormCheck.Label>
            </FormCheck>
            <FormCheck inline={true}>
              <FormCheck.Input
                checked={this.model.showAttachmentsThumbnails}
                type="checkbox"
                id="showAttachmentsThumbnails"
                className="sk-useMaterialInput ml-1"
                onChange={(checkbox: any) => (this.model.showAttachmentsThumbnails = checkbox.target.checked)}
                tabIndex={-1}
              />
              <FormCheck.Label htmlFor="showAttachmentsThumbnails">Show Thumbnails</FormCheck.Label>
            </FormCheck>
          </div>
          {['PIN1', 'PRO1'].includes(this.model.runMode) && (
            <div className="sk-keyboardInterface">
              {this.keyboardInterfaceActive ? (
                <span className="sk-keyboardInterfaceActive">WAITING FOR SHORTCUT KEY ...</span>
              ) : (
                <span>
                  CTRL+ALT to enter shortcut key{' '}
                  <OverlayTrigger placement="top" overlay={commandKeyPopover}>
                    <i className="zmdi zmdi-help" />
                  </OverlayTrigger>
                </span>
              )}
              <div className="sk-keyboardInterfaceActiveKey" id="sk-keyboardInterfaceActiveKey" />
            </div>
          )}
          {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
            <div className={`sk-moveOperationsControl ${this.model.moveOperationsModeActive ? 'fadeIn' : 'out'}`}>
              <div>
                Frist click any line(s) on any op to queue the line(s) to be moved. Previously issued lines qty{'>'}1
                can be split by specifying the qty to move after selecting the line.
              </div>
              <div className="sk-moveQueueCount">
                Selected Items: <strong>{this.model.moveOperationsQueue.size}</strong>
              </div>
              {this.model.moveOperationsInProgress ? (
                <div className="sk-moveOperationsInProgress">
                  <ClipLoader size={40} color="#ff8c00" />
                  <div className="sk-moveOperationsInProgressLabel">
                    {this.model.moveOperationsInProgressRepricing
                      ? 'Re-pricing queued items ...'
                      : 'Moving issued items ...'}
                  </div>
                </div>
              ) : (
                <>
                  <Button
                    variant="warning"
                    onClick={() => {
                      if (this.model.moveOperationsQueue.size === 0) {
                        toastr.error('Error', 'No items selected to move.');
                      } else {
                        this.model.showDestinationOperationSelectionModal = true;
                      }
                    }}
                  >
                    Select Destination Op
                  </Button>
                  <Button variant="secondary" onClick={() => (this.model.moveOperationsModeActive = false)}>
                    Cancel
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="sk-footerActions">
          <div className="sk-cancelButton">
            {['PIN1'].includes(this.model.runMode) && (
              <Dropdown drop="up" as={ButtonGroup}>
                <Button variant="danger" id="split-button-dropup" onClick={this.resetScreen}>
                  CANCEL
                </Button>

                <Dropdown.Toggle split={true} variant="danger" id="dropdown-split-basic" />

                <Dropdown.Menu>
                  <Dropdown.Item onClick={this.resetInvoiceBuilder}>Remove All Line Items</Dropdown.Item>
                  <Dropdown.Item
                    disabled={!this.model.ppcValidated}
                    onClick={() => {
                      this.showLostSaleReasonCodeSelectionModal = true;
                    }}
                  >
                    Record Lost Sale
                  </Dropdown.Item>
                  {process.env.NODE_ENV === 'development' && (
                    <Dropdown.Item onClick={() => sk.model.seed()}>Test Seed (Dev Only)</Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {['PIN4', 'PSO3'].includes(this.model.runMode) && (
              <Dropdown drop="up" as={ButtonGroup}>
                <Button variant="danger" id="split-button-dropup" onClick={this.resetScreen}>
                  CANCEL
                </Button>

                <Dropdown.Toggle split={true} variant="danger" id="dropdown-split-basic" />

                <Dropdown.Menu>
                  {[['PIN4'].includes(this.model.runMode)] && (
                    <Dropdown.Item
                      disabled={!this.model.ppcValidated}
                      onClick={() => {
                        this.showDeleteBillOfMaterialsModal = true;
                        setTimeout(() => {
                          if (this.deleteBOMListIDInputRef) {
                            this.deleteBOMListIDInputRef.focus();
                          }
                        }, 100);
                      }}
                    >
                      Delete Bill of Materials
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={this.resetInvoiceBuilder}>Remove All Line Items</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
              <Dropdown drop="up" as={ButtonGroup}>
                <Button variant="danger" id="split-button-dropup" onClick={this.resetScreen}>
                  RESET
                </Button>

                <Dropdown.Toggle split={true} variant="danger" id="dropdown-split-basic" />

                <Dropdown.Menu>
                  {this.model.runMode === 'PRO1' && !this.model.isEstimate && (
                    <Dropdown.Item
                      disabled={!this.model.ppcValidated || !this.model.activeRONumber}
                      onClick={() => {
                        this.showLostSaleReasonCodeSelectionModal = true;
                      }}
                    >
                      Record Lost Sale
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item
                    disabled={!this.model.activeRONumber}
                    onClick={() => {
                      this.model
                        .refreshActiveROData({ retainIssueQueue: 'ALLOPS', includeAttachments: true })
                        .then(() => toastr.success('Refreshed', 'Data Refreshed'));
                    }}
                  >
                    Refresh {this.model.isEstimate ? 'Estimate' : 'Repair Order'} Data
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          <div className="sk-middleButtons">
            {/* We'll enable these as they actually do something. */}

            {/* Only show this if we have defined cash/cod accounts in sysParams */}
            {['PIN1'].includes(this.model.runMode) && this.model.sysParams.codAccounts.length > 0 && (
              <DropdownButton
                variant="success"
                disabled={!this.model.ppcValidated}
                drop="up"
                title="Cash/COD"
                id="sk-CashCODDropUp"
              >
                {this.model.sysParams.codAccounts.map((codAccount, index) => (
                  <Dropdown.Item
                    eventKey={String(index)}
                    key={index}
                    onClick={async () => {
                      this.customerLookupRef.value = codAccount.codAccount;
                      if (await this.model.loadCustomerParams(codAccount.codAccount)) {
                        this.model.showShipToOverrideModal = true;
                      }
                    }}
                  >
                    {codAccount.codAccount} - {codAccount.codAccountName} (
                    {codAccount.taxable ? 'Taxable' : 'Non-Taxable'})
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            )}

            {/* Only show this if we have defined stock transfer accounts in sysParams */}
            {['PIN1'].includes(this.model.runMode) && this.model.sysParams.transferAccounts.length > 0 && (
              <DropdownButton
                variant="warning"
                disabled={!this.model.ppcValidated}
                drop="up"
                title="Transfer"
                id="sk-stockTransferDropUp"
              >
                {this.model.sysParams.transferAccounts.map((transferAccount, index) => (
                  <Dropdown.Item
                    eventKey={String(index)}
                    key={index}
                    onClick={async () => {
                      this.customerLookupRef.value = transferAccount.account;
                      await this.model.loadCustomerParams(transferAccount.account);
                    }}
                  >
                    {transferAccount.account} - {transferAccount.name} ({transferAccount.type.toUpperCase()})
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            )}

            {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
              <div className="sk-operationsButton">
                <Dropdown drop="up" as={ButtonGroup}>
                  <Button
                    variant="warning"
                    id="split-button-dropup"
                    disabled={this.model.activeRONumber === null}
                    onClick={() => (this.showOperationSelectionModal = true)}
                  >
                    Operations
                  </Button>
                  <Dropdown.Toggle
                    split={true}
                    disabled={this.model.activeRONumber === null}
                    variant="warning"
                    id="dropdown-split-basic"
                  />
                  <Dropdown.Menu>
                    {!['SMM3'].includes(this.model.runMode) && (
                      <Dropdown.Item
                        disabled={
                          !this.model.activeRONumber ||
                          this.model.activeOperation === 0 ||
                          this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].locked ||
                          this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].voided ||
                          !this.model.lineItems.some(
                            o => o.issuedToRO && o.roDetail!.operationNumber === this.model.activeOperation
                          )
                        }
                        onClick={() => {
                          modal.confirm({
                            title: 'Remove All',
                            message: `Are you SURE you wish to remove all items from operation ${this.model.activeOperation}?`,
                            callback: () => {
                              this.model.removeAllLineItemsFromActiveOperation();
                            },
                          });
                        }}
                      >
                        Remove All From Active Operation{' '}
                        {this.model.activeRONumber && this.model.activeOperation
                          ? `(${this.model.activeOperation})`
                          : ''}
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      onClick={() => {
                        this.model.moveOperationsModeActive = true;
                      }}
                    >
                      Move Line Items
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}

            {['PIN4'].includes(this.model.runMode) && (
              <Button disabled={!this.model.ppcValidated} onClick={() => this.loadSavedList('Bill of Materials')}>
                Load Bill of Materials
              </Button>
            )}

            {!['PIN4', 'SMM3'].includes(this.model.runMode) && (
              <DropdownButton
                variant="primary"
                disabled={!this.model.ppcValidated}
                drop="up"
                title={['PIN1'].includes(this.model.runMode) ? 'Picklists/Quotes' : 'Picklists'}
                id="sk-PickListQuotesDropUp"
              >
                <Dropdown.Item
                  disabled={
                    (['PIN1'].includes(this.model.runMode) && this.model.lineItems.length === 0) ||
                    (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) &&
                      !this.model.lineItems.some(o => !o.issuedToRO))
                  }
                  eventKey="1"
                  onClick={() => {
                    this.model.processByBinLocation = true;
                    if (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode)) {
                      this.processRO('pickticket');
                    } else {
                      this.process('pickticket');
                    }
                  }}
                >
                  Print Pick Ticket by Bin #
                </Dropdown.Item>
                <Dropdown.Item
                  disabled={
                    (['PIN1'].includes(this.model.runMode) && this.model.lineItems.length === 0) ||
                    (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) &&
                      !this.model.lineItems.some(o => !o.issuedToRO))
                  }
                  eventKey="2"
                  onClick={() => {
                    this.model.processByBinLocation = false;
                    if (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode)) {
                      this.processRO('pickticket');
                    } else {
                      this.process('pickticket');
                    }
                  }}
                >
                  Print Pick Ticket
                </Dropdown.Item>
                {['PIN1'].includes(this.model.runMode) && (
                  <Dropdown.Item
                    disabled={this.model.lineItems.length === 0}
                    eventKey="3"
                    onClick={() => {
                      // In the future, we might want quote by bin location
                      this.model.processByBinLocation = false;
                      this.process('quote');
                    }}
                  >
                    Print Quote
                  </Dropdown.Item>
                )}
                <Dropdown.Divider />
                <Dropdown.Item eventKey="4" onClick={() => this.loadSavedList('Bill of Materials')}>
                  Load Bill of Materials List
                </Dropdown.Item>
                <Dropdown.Item eventKey="5" onClick={() => this.loadSavedList('Pick List')}>
                  Load Pick List
                </Dropdown.Item>
                {['PIN1'].includes(this.model.runMode) && (
                  <Dropdown.Item eventKey="6" onClick={() => this.loadSavedList('Quote')}>
                    Load Quote
                  </Dropdown.Item>
                )}
              </DropdownButton>
            )}

            {['PIN1'].includes(this.model.runMode) && (
              <Button
                variant="primary"
                disabled={this.model.activeCustomerID === null}
                onClick={() => {
                  this.loadCustomerHistoryModal();
                }}
              >
                HISTORY/CREDITS
              </Button>
            )}

            {this.model.sysParams.loyalty && !['PIN4', 'PSO3', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
              <DropdownButton
                variant="primary"
                disabled={
                  !this.model.ppcValidated ||
                  this.model.activeCustomerID === null ||
                  this.model.isEstimate ||
                  (['PRO1'].includes(this.model.runMode) && !this.model.activeLoyaltyCustomerID) // available only if linked via SMM1 RO Open
                }
                drop="up"
                title={
                  <span>
                    {this.model.loyaltyOfferCount > 0 && <Badge variant="light">{this.model.loyaltyOfferCount}</Badge>}
                    Loyalty
                  </span>
                }
                id="sk-LoyaltyDropUp"
              >
                <Dropdown.Item
                  eventKey="1"
                  disabled={this.model.activeLoyaltyCustomerID === null}
                  onClick={() => {
                    this.loadLoyaltyOfferModal();
                  }}
                >
                  Coupon Check
                </Dropdown.Item>
                {!['PRO1'].includes(this.model.runMode) && (
                  <Dropdown.Item
                    eventKey="2"
                    disabled={this.model.activeCustomerID === null}
                    onClick={() => {
                      this.loadLoyaltyCustomerModal();
                    }}
                  >
                    Load/Create Loyalty Customer
                  </Dropdown.Item>
                )}
              </DropdownButton>
            )}

            {!['PIN4', 'PSO3', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
              <DropdownButton
                variant="primary"
                disabled={this.model.activeCustomerID === null || this.model.isEstimate}
                drop="up"
                title="BACKORDERS"
                id="sk-BackOrdersDropUp"
              >
                {this.model.lastBackorderData.confirmationNumber !== null && (
                  <Dropdown.Item
                    eventKey="1"
                    onClick={() => {
                      this.model.showBackorderPrinterSelectionModal = true;
                    }}
                  >
                    Print Last Backorder Documents
                  </Dropdown.Item>
                )}
                <Dropdown.Item eventKey="2" onClick={() => (this.showConfirmationStatusModal = true)}>
                  View Existing Backorder Status
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="3"
                  onClick={() => {
                    this.backorderType = 'NOW';
                    this.showBackorderModal = true;
                  }}
                >
                  Generate Emergency/Now Order
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="4"
                  onClick={() => {
                    this.backorderType = 'DAILY';
                    this.showBackorderModal = true;
                  }}
                >
                  Generate Daily Order
                </Dropdown.Item>
              </DropdownButton>
            )}

            {['PRO1', 'SMM3'].includes(this.model.runMode) &&
              this.model.subletOrUnpricedItemPendingIssue &&
              !this.model.isEstimate && (
                <Button
                  variant="warning"
                  onClick={() => {
                    this.showSubletUnpricedPOModal = true;
                  }}
                >
                  {this.model.subletOrUnpricedItemPendingIssue}
                </Button>
              )}

            {['PIN1'].includes(this.model.runMode) && (
              <Button
                variant="primary"
                disabled={this.model.activeCustomerID === null || this.model.customerParams.disableVariances}
                onClick={() => (this.showVariancePanel = true)}
              >
                VARIANCES
              </Button>
            )}
          </div>

          <div className="sk-processButton">
            {['PIN1'].includes(this.model.runMode) && (
              <Dropdown drop="up" as={ButtonGroup}>
                <Button
                  variant="success"
                  disabled={this.model.lineItems.length === 0}
                  id="split-button-dropup"
                  onClick={() => {
                    this.model.processByBinLocation = false;
                    this.process('invoice');
                  }}
                  style={{ minWidth: '125px' }}
                >
                  {this.model.isProcessing ? (
                    <>
                      <ClipLoader size={13} color="white" /> PROCESSING
                    </>
                  ) : (
                    'PROCESS'
                  )}
                </Button>

                <Dropdown.Toggle
                  split={true}
                  variant="success"
                  id="dropdown-split-basic"
                  disabled={this.model.lineItems.length === 0}
                />

                <Dropdown.Menu>
                  {!this.model.sysParams.noForcedCredits && (
                    <Dropdown.Item
                      onClick={() => {
                        this.process('manualcredit');
                      }}
                    >
                      Process Manual Credit
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item
                    onClick={() => {
                      this.model.processByBinLocation = true;
                      this.process('invoice');
                    }}
                  >
                    Process by Bin #
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {['PIN4'].includes(this.model.runMode) && (
              <Dropdown drop="up" as={ButtonGroup}>
                <Button
                  variant="success"
                  disabled={this.model.lineItems.length === 0}
                  id="split-button-dropup"
                  onClick={() => {
                    this.showSaveBillOfMaterialsModal = true;
                    setTimeout(() => {
                      if (this.saveBOMListIDInputRef) {
                        this.saveBOMListIDInputRef.focus();
                      }
                    }, 100);
                  }}
                  style={{ minWidth: '125px' }}
                >
                  SAVE
                </Button>

                <Dropdown.Toggle split={true} variant="success" id="dropdown-split-basic" disabled={true} />

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      /* */
                    }}
                  >
                    Future Action
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {['PSO3'].includes(this.model.runMode) && (
              <Dropdown drop="up" as={ButtonGroup}>
                <Button
                  variant="warning"
                  disabled={this.model.lineItems.length === 0}
                  id="split-button-dropup"
                  onClick={() => {
                    // Originally this was used for just sublet and unpriced POs, but we can tweak it for use here, too.
                    this.showSubletUnpricedPOModal = true;
                  }}
                  style={{ minWidth: '125px' }}
                >
                  Create Purchase Orders
                </Button>

                <Dropdown.Toggle split={true} variant="warning" id="dropdown-split-basic" disabled={true} />

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      // We'll inevitably need drop up actions.
                    }}
                  >
                    Future Placeholder (Disabled/Unseen)
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
              <>
                {this.model.activeDecisivCase && (
                  <Dropdown drop="up" as={ButtonGroup} className="decisivIssue">
                    <Button
                      variant="primary"
                      disabled={!this.model.activeOperationDecisivEnabled}
                      id="split-button-dropup"
                      onClick={() => {
                        this.processRO('decisivissue');
                      }}
                      style={{ minWidth: '125px' }}
                      title={
                        this.model.activeOperation === 0
                          ? '(Choose Operation)'
                          : `Operation ${this.model.activeOperation} Only`
                      }
                    >
                      {this.model.isDecisivROProcessing ? (
                        <>
                          <ClipLoader size={13} color="white" /> PROCESSING
                        </>
                      ) : (
                        'DECISIV ISSUE PARTS'
                      )}
                    </Button>

                    <Dropdown.Toggle
                      variant="primary"
                      split={true}
                      disabled={!this.model.activeOperationDecisivEnabled}
                      id="dropdown-split-basic"
                    />

                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => {
                          this.processRO('decisivpickticket');
                        }}
                      >
                        Decisiv Pick Ticket{' '}
                        {this.model.activeOperation === 0
                          ? '(Choose Operation)'
                          : `(Operation ${this.model.activeOperation} Only)`}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
                <Dropdown drop="up" as={ButtonGroup}>
                  <Button
                    variant="success"
                    disabled={
                      this.model.activeRONumber === null || !this.model.lineItems.some(lineItem => !lineItem.issuedToRO)
                    }
                    id="split-button-dropup"
                    onClick={() => {
                      this.processRO('issue');
                    }}
                    style={{ minWidth: '125px' }}
                  >
                    {this.model.isROProcessing ? (
                      <>
                        <ClipLoader size={13} color="white" /> PROCESSING
                      </>
                    ) : (
                      'ISSUE PARTS'
                    )}
                  </Button>

                  <Dropdown.Toggle
                    split={true}
                    variant="success"
                    id="dropdown-split-basic"
                    disabled={
                      this.model.activeRONumber === null || !this.model.lineItems.some(lineItem => !lineItem.issuedToRO)
                    }
                  />

                  <Dropdown.Menu>
                    <Dropdown.Item
                      disabled={!this.model.lineItems.some(lineItem => !lineItem.issuedToRO)}
                      onClick={() => {
                        this.processRO('negativeissue');
                      }}
                    >
                      Negative Issue
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            )}
          </div>
        </div>

        {/*
          The following are conditionally rendered component-based modals which
          fire due to UI actions (not initiated by the model). These are only
          rendered into the DOM when they are activated.
        */}

        {this.showBackorderModal && (
          <BackorderModal
            container={getContainer}
            hide={() => (this.showBackorderModal = false)}
            model={this.model}
            mode={this.backorderType}
            show={this.showBackorderModal}
          />
        )}

        {this.showCODCrossReferenceSelectionModal && (
          <CODCrossReferenceSelectionModal
            CODCrossReferences={this.lastCODCrossReferences}
            container={getContainer}
            hide={() => (this.showCODCrossReferenceSelectionModal = false)}
            show={this.showCODCrossReferenceSelectionModal}
            apply={(selectionIndex: number) => {
              this.showCODCrossReferenceSelectionModal = false;
              this.model.showShipToOverrideModal = false;

              const customerNum = this.lastCODCrossReferences[selectionIndex].itemid;
              this.loadCustomer(customerNum);
            }}
          />
        )}

        {this.showConfirmationStatusModal && (
          <BackorderStatus
            container={getContainer}
            hide={() => (this.showConfirmationStatusModal = false)}
            model={this.model}
            show={this.showConfirmationStatusModal}
          />
        )}

        {this.showEmailOptionsModal && (
          <EmailOptionsModal
            container={getContainer}
            hide={() => (this.showEmailOptionsModal = false)}
            model={this.model}
            show={this.showEmailOptionsModal}
          />
        )}

        {this.showFleetcrossModal && (
          <FleetcrossModal
            autoInquire={this.fleetcrossAutoInquire}
            container={getContainer}
            defaultValue={this.fleetcrossSeed}
            hide={() => (this.showFleetcrossModal = false)}
            show={this.showFleetcrossModal}
            model={this.model}
            onFleetcrossSelect={(partnumber: string, location: string) => {
              switch (location) {
                case 'INVENTORY':
                  this.showFleetcrossModal = false;
                  this.lineItemEntryRef.partLookupRef.value = partnumber;
                  break;
                case 'MASTER':
                  this.showFleetcrossModal = false;
                  this.lineItemEntryRef.partEntryInput.value = partnumber;
                  this.lineItemEntryRef.focusPartEntry(true);
                  break;
                case 'NONSTOCKING':
                  this.showFleetcrossModal = false;
                  this.lineItemEntryRef.partEntryInput.value = `@${partnumber}`;
                  this.lineItemEntryRef.focusPartEntry(true);
                  break;
                default:
                  break;
              }
            }}
          />
        )}

        {this.showCustomerHistoryModal && (
          <CustomerHistoryModal
            container={getContainer}
            hide={() => (this.showCustomerHistoryModal = false)}
            model={this.model}
            show={this.showCustomerHistoryModal}
          />
        )}

        {this.showCustomerVehiclesModal && (
          <CustomerVehiclesModal
            container={getContainer}
            hide={() => (this.showCustomerVehiclesModal = false)}
            model={this.model}
            show={this.showCustomerVehiclesModal}
          />
        )}

        {this.showLostSaleReasonCodeSelectionModal && (
          <LostSaleReasonCodeSelectionModal
            candidateParts={this.model.lostSaleCandidateParts}
            container={getContainer}
            hide={() => (this.showLostSaleReasonCodeSelectionModal = false)}
            onProcess={(parts, reasonCode) => {
              this.showLostSaleReasonCodeSelectionModal = false;
              this.model.recordLostSale(parts, reasonCode);
            }}
            lostSaleReasonCodes={this.model.sysParams.lostSaleReasonCodes}
            show={this.showLostSaleReasonCodeSelectionModal}
          />
        )}

        {this.showOperationSelectionModal && (
          <OperationSelectionModal
            container={getContainer}
            hide={() => (this.showOperationSelectionModal = false)}
            model={this.model}
            show={this.showOperationSelectionModal}
          />
        )}

        {this.showPendingConfirmationsModal && (
          <Modal
            backdrop="static"
            container={getContainer}
            show={this.showPendingConfirmationsModal}
            onHide={() => (this.showPendingConfirmationsModal = false)}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Parts Waiting</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode)
                ? 'This repair order has parts ready for issue.'
                : 'This customer has parts to pick up.'}
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={() => {
                  this.showPendingConfirmationsModal = false;
                  this.lineItemEntryRef.focusPartEntry();
                }}
                variant="secondary"
              >
                Not Now
              </Button>
              <Button
                autoFocus={true}
                variant="success"
                onClick={() => {
                  this.showPendingConfirmationsModal = false;
                  this.showConfirmationStatusModal = true;
                }}
              >
                Show Backorder Status
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {this.showVariancePanel && (
          <VariancePanelModal
            container={getContainer}
            hide={() => (this.showVariancePanel = false)}
            show={this.showVariancePanel}
            model={this.model}
          />
        )}

        {/*
          The following are conditionally rendered component-based modals which
          fire due to model interactions. These are only rendered into the DOM
          when they are activated.
        */}

        {this.model.showAlternateBranchesModal && (
          <Modal
            backdrop="static"
            size="lg"
            container={getContainer}
            onHide={() => (this.model.showAlternateBranchesModal = false)}
            show={this.model.showAlternateBranchesModal}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Branch Inventory</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              The data below may include supercedence parts and cross references.
              <table className="table" style={{ color: 'rgb(102,102,102)', marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Branch Desc</th>
                    <th>Bin</th>
                    <th>Part Num</th>
                    <th>Quantity</th>
                    <th>Months No Sale</th>
                    {this.model.sysParams.showNetOnBranchLookup && <th>Net</th>}
                  </tr>
                </thead>
                <tbody>
                  {this.model.activeBranchInventory.map((branch, index: number) => {
                    // Latest split supercedence method as of 3/22/19 to support per constituent on-hand.
                    // Branch arrays indicate inferred split supercedences.
                    if (Array.isArray(branch)) {
                      return (
                        <tr className="sk-selectorRow" key={index}>
                          <td>{branch[0].location}</td>
                          <td>{this.model.branchTitleDict[branch[0].location] || ''}</td>
                          <td>
                            <div>&nbsp;</div>
                            {branch.map((branchConstituent, binIndex) => (
                              <div key={`sk-binIndex-${binIndex}`}>{branchConstituent.bin}</div>
                            ))}
                          </td>
                          <td>
                            <div>
                              <i>Split Supercedence:</i>
                            </div>
                            {branch.map((branchConstituent, pnIndex) => (
                              <div key={`sk-pnIndex-${pnIndex}`}>{branchConstituent.partNum}</div>
                            ))}
                          </td>
                          <td>
                            <div>&nbsp;</div>
                            {branch.map((branchConstituent, ohIndex) => (
                              <div key={`sk-ohIndex-${ohIndex}`}>{branchConstituent.onHand}</div>
                            ))}
                          </td>
                          <td>
                            <div>&nbsp;</div>
                            {branch.map((branchConstituent, mnsIndex) => (
                              <div key={`sk-mnsIndex-${mnsIndex}`}>{branchConstituent.monthsNoSale}</div>
                            ))}
                          </td>
                          {this.model.sysParams.showNetOnBranchLookup && (
                            <td>
                              <div>&nbsp;</div>
                              {branch.map((branchConstituent, npIndex) => (
                                <div key={`sk-npIndex-${npIndex}`}>{branchConstituent.netPrice}</div>
                              ))}
                            </td>
                          )}
                        </tr>
                      );
                    } else {
                      // The split supercedence support below was our original method (and is likely unused now).
                      return (
                        <tr className="sk-selectorRow" key={index}>
                          <td>{branch.location}</td>
                          <td>{this.model.branchTitleDict[branch.location] || ''}</td>
                          <td>{branch.bin}</td>
                          <td>
                            {Array.isArray(branch.partNum) ? (
                              <>
                                <i>Split Supercedence:</i>
                                <br />
                                {branch.partNum.map((partNumConstituent, index2) => (
                                  <div key={`${index}-${index2}`}>{partNumConstituent}</div>
                                ))}
                              </>
                            ) : (
                              branch.partNum
                            )}
                          </td>
                          <td>{branch.onHand}</td>
                          <td>{branch.monthsNoSale}</td>
                          {this.model.sysParams.showNetOnBranchLookup && <td>{branch.netPrice}</td>}
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => (this.model.showAlternateBranchesModal = false)} variant="secondary">
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {/* For printing confirmations / purchase orders produced by the backorder system. */}
        {this.model.showBackorderPrinterSelectionModal && (
          <PrinterSelectionBOModal
            container={getContainer}
            hide={() => (this.model.showBackorderPrinterSelectionModal = false)}
            model={this.model}
            show={this.model.showBackorderPrinterSelectionModal}
          />
        )}

        {/* For printing standalone purchase orders for sublets & unpriced parts. */}
        {this.model.showStandalonePOPrinterSelectionModal && (
          <PrinterSelectionBOModal
            container={getContainer}
            hide={() => (this.model.showStandalonePOPrinterSelectionModal = false)}
            model={this.model}
            show={this.model.showStandalonePOPrinterSelectionModal}
            standalonePO={true}
          />
        )}

        {/*
          Note this is only used if cash sale printers are defined.
          Cash sales will revert to the regular invoice printer list on clients
          that do not explicitly define cash sale printers.
        */}
        {this.model.showCashSalePrinterSelectionModal && (
          <PrinterSelectionModal
            // activePrinter will be an {id, name} object or null if not previously set
            activePrinterDefault={JSON.parse(localStorage.getItem('blazeCashSalePrinter')!)}
            container={getContainer}
            hide={() => {
              this.model.showCashSalePrinterSelectionModal = false;
              this.model.lastManualCreditAuth = null; // prevents clever clerks from hijacking manager manual credit auth
            }}
            onPrinterSelection={async (id: string, name: string) => {
              this.model.showCashSalePrinterSelectionModal = false;

              // save the chosen printer for later so users can enter right past
              // the modal on future invoice prints
              localStorage.setItem(
                'blazeCashSalePrinter',
                JSON.stringify({
                  id,
                  name,
                })
              );

              const res = await this.model.process({
                printer: {
                  id,
                  name,
                },
                type: this.model.lastProcessType, // invoice or manualcredit
              });

              if (res.success) {
                this.resetScreen();
              }
            }}
            printerList={this.model.sysParams.cashSalePrinters}
            screenPrinterID={this.model.sysParams.screenPrinters.cashSale}
            show={this.model.showCashSalePrinterSelectionModal}
          />
        )}

        {this.model.showCreditInvoiceModal && (
          <CreditInvoiceModal
            container={getContainer}
            closeCustomerHistoryModal={() => (this.showCustomerHistoryModal = false)}
            hide={() => (this.model.showCreditInvoiceModal = false)}
            model={this.model}
            show={this.model.showCreditInvoiceModal}
          />
        )}

        {this.model.showConfirmationModal && (
          <ConfirmationModal
            container={getContainer}
            closeConfirmationStatusModal={() => (this.showConfirmationStatusModal = false)}
            hide={() => (this.model.showConfirmationModal = false)}
            model={this.model}
            show={this.model.showConfirmationModal}
          />
        )}

        {this.model.showConfirmationSaleShipToModal && (
          <ConfirmationShipToSelectionModal
            container={getContainer}
            hide={() => (this.model.showConfirmationSaleShipToModal = false)}
            model={this.model}
            show={this.model.showConfirmationSaleShipToModal}
          />
        )}

        {this.model.showCreditHoldPostOpModal && (
          <CreditHoldPostOpModal
            container={getContainer}
            defaultScreenPrintEmail={this.model.creditHoldPostOp.defaultScreenPrintEmail}
            hide={() => (this.model.showCreditHoldPostOpModal = false)}
            holdNumber={this.model.creditHoldPostOp.holdNumber}
            onPostOpProcess={async creditHoldPostOpFields => {
              this.model.showCreditHoldPostOpModal = false;
              const res = await this.model.process({
                creditHoldPostOpFields,
                printer: this.model.creditHoldPostOp.printer, // selection has already been made
                type: 'invoice',
              });
              if (res.success) {
                this.resetScreen();
              }
            }}
            onPrintPickTicket={() => {
              this.model.processByBinLocation = false;
              this.process('pickticket');
            }}
            postOpActions={this.model.creditHoldPostOp.actions}
            show={this.model.showCreditHoldPostOpModal}
          />
        )}

        {this.model.showFleetBillingPostOpModal && (
          <FleetBillingPostOpModal
            container={getContainer}
            hide={() => (this.model.showFleetBillingPostOpModal = false)}
            onPostOpProcess={async (fleetBillingPostOpFields: string[]) => {
              this.model.showFleetBillingPostOpModal = false;
              const res = await this.model.process({
                fleetBillingPostOpFields,
                printer: this.model.fleetBillingPostOp.printer, // selection has already been made
                type: 'invoice',
              });
              if (res.success) {
                this.resetScreen();
              }
            }}
            postOpFieldsRequested={this.model.fleetBillingPostOp.fields}
            show={this.model.showFleetBillingPostOpModal}
          />
        )}

        {/* This is used by the invoice credits system only, not manual credits. */}
        {this.model.showCreditInvoicePrinterSelectionModal && (
          <PrinterSelectionModal
            /* activePrinter will be an {id, name} object or null if not previously set.
               Default to the standard invoice printer on first use (if previously saved, probable),
               after which, credit invoices can be set/saved to a separate printer if desired.
            */
            activePrinterDefault={
              JSON.parse(localStorage.getItem('blazeCreditInvoicePrinter')!) ||
              JSON.parse(localStorage.getItem('blazeInvoicePrinter')!)
            }
            container={getContainer}
            hide={() => (this.model.showCreditInvoicePrinterSelectionModal = false)}
            onPrinterSelection={(id: string, name: string) => {
              this.model.showCreditInvoicePrinterSelectionModal = false;

              // save the chosen printer for later so users can enter right past
              // the modal on future credit invoice prints
              localStorage.setItem(
                'blazeCreditInvoicePrinter',
                JSON.stringify({
                  id,
                  name,
                })
              );

              this.model.processCreditInvoice({ id, name });
            }}
            printerList={this.model.sysParams.invoicePrinters}
            screenPrinterID={this.model.sysParams.screenPrinters.invoice}
            show={this.model.showCreditInvoicePrinterSelectionModal}
          />
        )}

        {this.model.showInvoicePrinterSelectionModal && (
          <PrinterSelectionModal
            // activePrinter will be an {id, name} object or null if not previously set
            activePrinterDefault={JSON.parse(localStorage.getItem('blazeInvoicePrinter')!)}
            container={getContainer}
            hide={() => {
              this.model.showInvoicePrinterSelectionModal = false;
              this.model.lastManualCreditAuth = null; // prevents clever clerks from hijacking manager manual credit auth
            }}
            onPrinterSelection={async (id: string, name: string) => {
              this.model.showInvoicePrinterSelectionModal = false;

              // save the chosen printer for later so users can enter right past
              // the modal on future invoice prints
              localStorage.setItem(
                'blazeInvoicePrinter',
                JSON.stringify({
                  id,
                  name,
                })
              );

              const res = await this.model.process({
                printer: {
                  id,
                  name,
                },
                type: this.model.lastProcessType, // invoice or manualcredit
              });

              if (res.success) {
                this.resetScreen();
              }
            }}
            printerList={this.model.sysParams.invoicePrinters}
            screenPrinterID={this.model.sysParams.screenPrinters.invoice}
            show={this.model.showInvoicePrinterSelectionModal}
          />
        )}

        {this.model.showDestinationOperationSelectionModal && (
          <OperationSelectionModal
            container={getContainer}
            hide={() => (this.model.showDestinationOperationSelectionModal = false)}
            model={this.model}
            show={this.model.showDestinationOperationSelectionModal}
            moveToOperationMode={true}
          />
        )}

        {this.model.showManualCreditAuthModal && (
          <Modal
            backdrop="static"
            container={getContainer}
            dialogClassName="sk-manualCreditAuthModal"
            show={this.model.showManualCreditAuthModal}
            onHide={() => (this.model.showManualCreditAuthModal = false)}
            size="sm"
          >
            <Modal.Header>
              <Modal.Title>Authorization Required</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="sk-manualCreditsAuthContainer">
                <input
                  type="text"
                  placeholder="PPC"
                  className="sk-manualCreditAuthPPCInput"
                  defaultValue={this.model.canClerkPPCIssueCredit ? this.model.ppcParams.ppc : ''}
                  ref={(ref: HTMLInputElement) => (this.manualCreditAuthPpcInput = ref)}
                />
                <input
                  type="password"
                  placeholder="Credits Password"
                  className="sk-manualCreditAuthPPCPWInput"
                  ref={(ref: HTMLInputElement) => (this.manualCreditAuthPasswordInput = ref)}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => (this.model.showManualCreditAuthModal = false)} variant="secondary" tabIndex={-1}>
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={async () => {
                  const manualCreditAuthPpc = this.manualCreditAuthPpcInput.value.trim();
                  const manualCreditAuthPassword = this.manualCreditAuthPasswordInput.value.trim();

                  // Credit auth modal should enforce this
                  if (!!manualCreditAuthPpc && !!manualCreditAuthPassword) {
                    const validated = await this.model.validatePPCCreditAuth(
                      manualCreditAuthPpc,
                      manualCreditAuthPassword
                    );
                    if (validated) {
                      this.model.showManualCreditAuthModal = false;
                      this.model.lastManualCreditAuth = {
                        ppc: manualCreditAuthPpc,
                        password: manualCreditAuthPassword,
                      };
                      this.process('manualcredit');
                    } // else validation routine will toast specific errors
                  } else {
                    toastr.error('Error', 'PPC Credit Auth Details Required');
                  }
                }}
              >
                Proceed
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {this.model.showPickTicketPrinterSelectionModal && (
          <>
            {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) ? (
              <PrinterSelectionModal
                // this will only be hit by in-house pick tickets, not decisiv
                // activePrinter will be an {id, name} object or null if not previously set
                activePrinterDefault={JSON.parse(localStorage.getItem('blazeROPickTicketPrinter')!)}
                container={getContainer}
                hide={() => (this.model.showPickTicketPrinterSelectionModal = false)}
                onPrinterSelection={(id: string, name: string) => {
                  this.model.showPickTicketPrinterSelectionModal = false;

                  // save the chosen printer for later so users can enter right past
                  // the modal on future invoice prints
                  localStorage.setItem(
                    'blazeROPickTicketPrinter',
                    JSON.stringify({
                      id,
                      name,
                    })
                  );

                  this.model.processRO({
                    printer: {
                      id,
                      name,
                    },
                    type: 'pickticket',
                  });
                }}
                printerList={this.model.sysParams.roPickTicketPrinters}
                screenPrinterID={this.model.sysParams.screenPrinters.roPickTicket}
                show={this.model.showPickTicketPrinterSelectionModal}
              />
            ) : (
              <PrinterSelectionModal
                // PIN1 pickticket printer selection
                // activePrinter will be an {id, name} object or null if not previously set
                activePrinterDefault={JSON.parse(localStorage.getItem('blazePickTicketPrinter')!)}
                container={getContainer}
                hide={() => (this.model.showPickTicketPrinterSelectionModal = false)}
                onPrinterSelection={(id: string, name: string) => {
                  this.model.showPickTicketPrinterSelectionModal = false;

                  // save the chosen printer for later so users can enter right past
                  // the modal on future invoice prints
                  localStorage.setItem(
                    'blazePickTicketPrinter',
                    JSON.stringify({
                      id,
                      name,
                    })
                  );

                  this.model.process({
                    printer: {
                      id,
                      name,
                    },
                    type: 'pickticket',
                  });
                }}
                printerList={this.model.sysParams.pickTicketPrinters}
                screenPrinterID={this.model.sysParams.screenPrinters.pickTicket}
                show={this.model.showPickTicketPrinterSelectionModal}
              />
            )}
          </>
        )}

        {this.model.showProcessLoyaltyRedemptionsModal && (
          <ProcessLoyaltyRedemptionsModal
            cancel={() => this.model.processLoyaltyRedemptionsCancel()}
            container={getContainer}
            hide={() => {
              // only enable this action if processed/cancelled
              this.model.showProcessLoyaltyRedemptionsModal = false;
            }}
            model={this.model}
            removeUnredeemedAndProcessInvoice={() => {
              /* 1. dismiss this modal
                 2. remove un-redeemed offers
                 3. update taxes
                 4. process invoice

                 Any previously successful redemptions will remain in tact with this method. If the user wanted
                 to remove successful redemptions at this point (why?), they could select the "back to invoice"
                 option and manually remove them before continuing.  Nightly void process would eventually handle
                 the void.
              */
              this.model.showProcessLoyaltyRedemptionsModal = false;
              this.model.removeUnredeemedAndProcessInvoice();
            }}
            show={this.model.showProcessLoyaltyRedemptionsModal}
            tryAgain={() => {
              // this method automatically only tries lines with no paired offer redemptions
              // already redeemed lines will be left as is
              this.model.processLoyaltyRedemptions();
            }}
          />
        )}

        {this.model.showQuotePrinterSelectionModal && (
          <PrinterSelectionModal
            // activePrinter will be an {id, name} object or null if not previously set
            activePrinterDefault={JSON.parse(localStorage.getItem('blazeQuotePrinter')!)}
            container={getContainer}
            quoteParams={{
              defaultDays: parseInt(this.model.sysParams.quoteExpirationDefaultDays, 10) || 120,
              maxDays: parseInt(this.model.sysParams.quoteExpirationMaximumDays, 10) || 120,
              hidePartNumbers: JSON.parse(localStorage.getItem('blazeQuoteHidePartNumbers') || 'false'),
            }}
            hide={() => (this.model.showQuotePrinterSelectionModal = false)}
            onPrinterSelection={async (
              id: string,
              name: string,
              expirationDate?: string,
              hidePartNumbers?: boolean,
              quoteTitle?: string
            ) => {
              this.model.showQuotePrinterSelectionModal = false;

              // save the chosen printer for later so users can enter right past
              // the modal on future invoice prints
              localStorage.setItem(
                'blazeQuotePrinter',
                JSON.stringify({
                  id,
                  name,
                })
              );

              // save hide part numbers option selection as well
              localStorage.setItem('blazeQuoteHidePartNumbers', JSON.stringify(hidePartNumbers));

              await this.model.process({
                expirationDate,
                hidePartNumbers,
                quoteTitle,
                printer: {
                  id,
                  name,
                },
                type: 'quote',
              });
            }}
            // Quotes use the same printer list as confirmations, if defined on this client.
            // Fall back to the invoice printer list if this client does not specify.
            printerList={
              this.model.sysParams.confirmationQuotePrinters.length > 0
                ? this.model.sysParams.confirmationQuotePrinters
                : this.model.sysParams.invoicePrinters
            }
            screenPrinterID={
              this.model.sysParams.confirmationQuotePrinters.length > 0
                ? this.model.sysParams.screenPrinters.confirmationQuote
                : this.model.sysParams.screenPrinters.invoice
            }
            show={this.model.showQuotePrinterSelectionModal}
          />
        )}

        {this.model.showScreenPrintModal && (
          <ScreenPrintModal
            container={getContainer}
            docs={this.model.screenPrintDocs}
            hide={() => (this.model.showScreenPrintModal = false)}
            show={this.model.showScreenPrintModal}
            signatureCapture={this.model.sysParams.signatureCapture && this.model.screenPrintIsInvoice}
            signatureCaptureDocID={this.model.lastDocID}
          />
        )}

        {this.model.showShipToOverrideModal && (
          <ShipToOverrideModal
            codAccount={this.model.customerParams.isCodAccount}
            codAccountDefaults={
              this.model.customerParams.isCodAccount
                ? {
                    city: this.model.customerParams.city,
                    state: this.model.customerParams.state,
                    zipcode: this.model.customerParams.zipcode
                      ? this.model.customerParams.zipcodeSignature
                        ? `${this.model.customerParams.zipcode}.${this.model.customerParams.zipcodeSignature}`
                        : this.model.customerParams.zipcode
                      : '',
                  }
                : null
            }
            container={getContainer}
            hide={() => (this.model.showShipToOverrideModal = false)}
            hideInactiveAccounts={this.model.sysParams.hideInactiveAccounts}
            show={this.model.showShipToOverrideModal}
            shipToAccountID={this.model.shipToAccount ? this.model.shipToAccount.customerID : ''}
            shipToAccountDetails={this.model.shipToAccount ? this.model.shipToAccount : null}
            shipToEmail={this.model.shipToEmail}
            shipToAddress={this.model.shipToAddress}
            showAdditionalNames={this.model.sysParams.showName23}
            showCODCrossReferences={(CODCrossReferences: ICustomerLookupRecord[]) => {
              this.lastCODCrossReferences = CODCrossReferences;
              this.showCODCrossReferenceSelectionModal = true;
            }}
            clearShipToOverride={() => {
              this.model.clearShipToOverride();
              this.model.showShipToOverrideModal = false;
              this.lineItemEntryRef.focusPartEntry();
            }}
            loadShipToAccount={(customerID: string, details: ICustomerParams, shipToEmail: boolean) => {
              this.model.setShipToAccount(customerID, details, shipToEmail);
              this.model.showShipToOverrideModal = false;
              this.lineItemEntryRef.focusPartEntry();
            }}
            loadShipToAddress={(details: IShipToAddress) => {
              this.model.setShipToAddress(details);
              this.model.showShipToOverrideModal = false;
              this.lineItemEntryRef.focusPartEntry();
            }}
            useZipcodeList={this.model.sysParams.hasZipcodeList}
          />
        )}

        {/* TODO: Externalize the load saved list component. (Sorted out of order here on purpose due to code length.) */}
        {this.showLoadSavedListModal && (
          <Modal
            backdrop="static"
            container={getContainer}
            show={this.showLoadSavedListModal}
            onHide={() => (this.showLoadSavedListModal = false)}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Load {this.savedListType}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                <Label>{this.savedListType} ID</Label>
                <input
                  autoFocus={true} // ignored in BS4
                  className="form-control sk-useMaterialInput"
                  defaultValue={this.savedListIDSeed}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter' && this.savedListIDInputRef.value.trim().length > 0) {
                      this.showLoadSavedListModal = false;
                      this.model.loadSavedList(this.savedListIDInputRef.value, this.savedListType);
                    }
                  }}
                  ref={(ref: HTMLInputElement) => (this.savedListIDInputRef = ref)}
                  style={{ width: '100%' }}
                  type="text"
                />
              </div>
              {this.model.runMode === 'PIN1' && (
                <>
                  {this.savedListType === 'Pick List' &&
                    this.model.activeCustomerID &&
                    !this.model.customerPickListsLazyLoaded && (
                      <div style={{ marginTop: '30px' }}>
                        <LoadingPage />
                      </div>
                    )}
                  {this.savedListType === 'Pick List' &&
                    this.model.activeCustomerID &&
                    this.model.customerPickListsLazyLoaded &&
                    this.model.customerPickLists.length > 0 && (
                      <div style={{ marginTop: '30px', maxHeight: '300px', overflow: 'auto' }}>
                        <Label>Previously linked to customer {this.model.activeCustomerID}:</Label>
                        <table className="table" style={{ marginTop: '10px' }}>
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>CREATED</th>
                            </tr>
                          </thead>
                          <tbody>
                            {this.model.customerPickLists.map((pickList, index) => {
                              return (
                                <tr
                                  className="sk-selectorRow"
                                  key={index}
                                  onClick={() => {
                                    this.showLoadSavedListModal = false;
                                    this.model.loadSavedList(pickList.id, this.savedListType);
                                  }}
                                >
                                  <td>{pickList.id}</td>
                                  <td>{pickList.createdAt}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                </>
              )}
              {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
                <>
                  {this.savedListType === 'Pick List' &&
                    this.model.activeRONumber &&
                    !this.model.roPickListsLazyLoaded && (
                      <div style={{ marginTop: '30px' }}>
                        <LoadingPage />
                      </div>
                    )}
                  {this.savedListType === 'Pick List' &&
                    this.model.activeRONumber &&
                    this.model.roPickListsLazyLoaded &&
                    this.model.roPickLists.length > 0 && (
                      <div style={{ marginTop: '30px', maxHeight: '300px', overflow: 'auto' }}>
                        <Label>Previously linked to repair order {this.model.activeRONumber}:</Label>
                        <table className="table" style={{ marginTop: '10px' }}>
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>CREATED</th>
                            </tr>
                          </thead>
                          <tbody>
                            {this.model.roPickLists.map((pickList, index) => {
                              return (
                                <tr
                                  className="sk-selectorRow"
                                  key={index}
                                  onClick={() => {
                                    this.showLoadSavedListModal = false;
                                    this.model.loadSavedList(pickList.id, this.savedListType);
                                  }}
                                >
                                  <td>{pickList.id}</td>
                                  <td>{pickList.createdAt}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                </>
              )}
              {this.savedListType === 'Quote' &&
                this.model.activeCustomerID &&
                this.model.customerParams.quotes.length > 0 && (
                  <div style={{ marginTop: '30px', maxHeight: '300px', overflow: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>CREATED</th>
                          <th>EXPIRES</th>
                          <th>TITLE</th>
                          <th>PPC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.model.customerParams.quotes.map((quote, index) => {
                          return (
                            <tr
                              className="sk-selectorRow"
                              key={index}
                              onClick={() => {
                                this.showLoadSavedListModal = false;
                                this.model.loadSavedList(quote.quoteNo, this.savedListType);
                              }}
                            >
                              <td>{quote.quoteNo}</td>
                              <td>{quote.quoteDate}</td>
                              <td>{quote.quoteExpiration}</td>
                              <td>{quote.title}</td>
                              <td>{quote.quotePPC}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              <br />
              {_.includes(['Pick List', 'Quote'], this.savedListType) && this.model.runMode === 'PIN1' && (
                <span style={{ color: 'red' }}>This action will reset the invoice builder.</span>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => (this.showLoadSavedListModal = false)} variant="secondary">
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  if (this.savedListIDInputRef.value.trim().length > 0) {
                    this.showLoadSavedListModal = false;
                    this.model.loadSavedList(this.savedListIDInputRef.value, this.savedListType);
                  } else {
                    this.savedListIDInputRef.focus();
                  }
                }}
              >
                Proceed
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {this.showSaveBillOfMaterialsModal && (
          <Modal
            backdrop="static"
            container={getContainer}
            show={this.showSaveBillOfMaterialsModal}
            onHide={() => (this.showSaveBillOfMaterialsModal = false)}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Save Bill of Materials</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ marginBottom: '10px' }}>
                <Label>Name</Label>
                <input
                  autoFocus={true} // ignored in BS4
                  className="form-control sk-useMaterialInput"
                  defaultValue={this.model.lastBOMLoad ? this.model.lastBOMLoad.listID : ''}
                  ref={(ref: HTMLInputElement) => (this.saveBOMListIDInputRef = ref)}
                  style={{ width: '100%' }}
                  maxLength={18}
                  type="text"
                />
              </div>
              <div>
                <Label>Description</Label>
                <input
                  autoFocus={true} // ignored in BS4
                  className="form-control sk-useMaterialInput"
                  defaultValue={this.model.lastBOMLoad ? this.model.lastBOMLoad.listTitle : ''}
                  ref={(ref: HTMLInputElement) => (this.saveBOMListTitleInputRef = ref)}
                  style={{ width: '100%' }}
                  maxLength={60}
                  type="text"
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => (this.showSaveBillOfMaterialsModal = false)} variant="secondary">
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  // validations and save / update
                  const listID = this.saveBOMListIDInputRef.value.trim(); // Name on PIN6
                  const listTitle = this.saveBOMListTitleInputRef.value.trim(); // Description on PIN6

                  // Only the ID (Name) is required
                  if (listID.length > 0) {
                    if (listID.includes(' ')) {
                      toastr.error('Error', 'Name Cannot Include Spaces');
                      return;
                    }

                    if (listID.length > 18) {
                      toastr.error('Error', 'Name Cannot Exceed 18 Characters');
                      return;
                    }

                    if (listTitle.length > 18) {
                      toastr.error('Error', 'Description Cannot Exceed 60 Characters');
                      return;
                    }

                    this.showSaveBillOfMaterialsModal = false;

                    // This is a fast op, no real need to show a spinner.
                    this.model.saveBillOfMaterials(listID, listTitle);
                  } else {
                    toastr.error('Error', 'BOM Name Required');
                  }
                }}
              >
                Save
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {this.showDeleteBillOfMaterialsModal && (
          <Modal
            backdrop="static"
            container={getContainer}
            show={this.showDeleteBillOfMaterialsModal}
            onHide={() => (this.showDeleteBillOfMaterialsModal = false)}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Delete Bill of Materials</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ marginBottom: '10px' }}>
                <Label>Name</Label>
                <input
                  autoFocus={true} // ignored in BS4
                  className="form-control sk-useMaterialInput"
                  defaultValue={this.model.lastBOMLoad ? this.model.lastBOMLoad.listID : ''}
                  ref={(ref: HTMLInputElement) => (this.deleteBOMListIDInputRef = ref)}
                  style={{ width: '100%' }}
                  maxLength={18}
                  type="text"
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => (this.showDeleteBillOfMaterialsModal = false)} variant="secondary">
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  // validations and save / update
                  const listID = this.deleteBOMListIDInputRef.value.trim(); // Name on PIN6
                  if (listID.length > 0) {
                    this.model.saveBillOfMaterials(listID, '', true);
                    this.showDeleteBillOfMaterialsModal = false;
                  } else {
                    toastr.error('Error', 'Specify list name to delete.');
                  }
                }}
              >
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {this.showSubletUnpricedPOModal && (
          <SubletUnpricedPOModal
            container={getContainer}
            hide={() => (this.showSubletUnpricedPOModal = false)}
            model={this.model}
            show={this.showSubletUnpricedPOModal}
          />
        )}
      </div>
    );
  }
}

class ErrorBoundary extends React.Component<any, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: any, info: any) {
    this.setState({ hasError: true });

    console.log(error);
    console.log(info);

    // Note React error boundaries do not catch exceptions within event handlers
    if (process.env.NODE_ENV !== 'development') {
      Sentry.setExtras({ info });
      Sentry.captureException(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ margin: '15px' }}>
          <h4>Unrecoverable error :(</h4>
          Please advise DSI Solutions which part number / screen action caused this error.
        </div>
      );
    }
    return this.props.children;
  }
}

export default (props: IProps) => {
  return (
    <ErrorBoundary>
      <ShopKeeper {...props} />
    </ErrorBoundary>
  );
};
